'use client';

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createClient } from '@/lib/supabase/client';
import Link from 'next/link';
import { Video, VIDEO_CATEGORIES } from '@/data/videos';
import { uploadVideoToStorage, uploadThumbnailToStorage } from '@/lib/supabase/storage';
import AdminCommentModeration from './AdminCommentModeration';

interface AdminPanelProps {
  pendingVideos: Video[];
  onApprove: (id: string) => void;
  onReject: (id: string) => void;
  onClose: () => void;
  initialEditPostId?: string;
}

interface UserProfile {
  id: string;
  email: string;
  full_name: string;
  username?: string;
  avatar_url?: string;
  created_at?: string;
  is_admin?: boolean;
}

interface EditUserForm {
  full_name: string;
  username: string;
  avatar_url: string;
}

interface DbVideo {
  id: string;
  title: string;
  tag: string;
  description?: string;
  source: string;
  url?: string;
  embed_id?: string;
  thumbnail?: string;
  thumbnail_alt?: string;
  views?: number;
  likes_count?: number;
  submitted_by?: string;
  submitted_at?: string;
  video_status?: string;
  category?: string;
}

type AdminTab = 'pending' | 'videos' | 'upload' | 'users' | 'admins' | 'featured' | 'ads' | 'news' | 'advertisers' | 'approvals' | 'revenue' | 'comments' | 'reports' | 'guide';
type UploadSourceType = 'file' | 'youtube' | 'vimeo';

interface AdRecord {
  id: string;
  title: string;
  advertiser: string;
  image_url: string;
  image_alt: string;
  link_url: string;
  placement: string;
  is_active: boolean;
  notes: string;
  created_at?: string;
}

interface NewsPost {
  id: string;
  title: string;
  slug: string;
  excerpt?: string;
  content?: string;
  cover_image?: string;
  cover_image_alt?: string;
  category?: string;
  categories?: string[];
  tags?: string[];
  author?: string;
  is_published?: boolean;
  is_featured?: boolean;
  views?: number;
  created_at?: string;
  scheduled_at?: string | null;
  meta_title?: string;
  meta_description?: string;
  og_image?: string;
}

interface Advertiser {
  id: string;
  company_name: string;
  contact_name: string;
  email: string;
  phone?: string;
  website?: string;
  ad_budget?: number;
  account_status: 'pending' | 'approved' | 'rejected' | 'suspended';
  notes?: string;
  rejection_reason?: string;
  approved_at?: string;
  approved_by?: string;
  created_at?: string;
}

interface RevenueSettlement {
  id: string;
  advertiser_id: string;
  period_start: string;
  period_end: string;
  total_impressions: number;
  total_clicks: number;
  amount_due: number;
  amount_paid: number;
  payment_status: 'pending' | 'paid' | 'overdue' | 'cancelled';
  invoice_number?: string;
  notes?: string;
  paid_at?: string;
  created_at?: string;
  advertisers?: { company_name: string };
}

interface AdPaymentRecord {
  id: string;
  billing_name: string;
  billing_email: string;
  company_name: string | null;
  plan: string;
  ad_type: string;
  placement: string | null;
  amount: number;
  currency: string;
  payment_status: string;
  payment_intent_id: string | null;
  campaign_start: string | null;
  campaign_end: string | null;
  created_at: string;
}

interface VideoReport {
  id: string;
  video_id: string;
  category: string;
  details?: string;
  reporter_id?: string;
  reporter_email?: string;
  status: 'pending' | 'reviewed' | 'dismissed' | 'removed';
  admin_notes?: string;
  resolved_by?: string;
  resolved_at?: string;
  action_taken?: string;
  created_at: string;
  // joined
  video_title?: string;
  video_thumbnail?: string;
  video_submitted_by?: string;
}

export default function AdminPanel({ pendingVideos, onApprove, onReject, onClose, initialEditPostId }: AdminPanelProps) {
  const [activeTab, setActiveTab] = useState<AdminTab>('pending');
  const [password, setPassword] = useState('');
  const [authenticated, setAuthenticated] = useState(false);
  const [authError, setAuthError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [authLoading, setAuthLoading] = useState(false);

  // Video preview state
  const [previewVideo, setPreviewVideo] = useState<Video | null>(null);

  // Users state
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [usersError, setUsersError] = useState('');
  const [editingUser, setEditingUser] = useState<UserProfile | null>(null);
  const [editForm, setEditForm] = useState<EditUserForm>({ full_name: '', username: '', avatar_url: '' });
  const [editLoading, setEditLoading] = useState(false);
  const [editError, setEditError] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [successMsg, setSuccessMsg] = useState('');

  // Manage Videos state
  const [allVideos, setAllVideos] = useState<DbVideo[]>([]);
  const [videosLoading, setVideosLoading] = useState(false);
  const [videosError, setVideosError] = useState('');
  const [videoDeleteConfirmId, setVideoDeleteConfirmId] = useState<string | null>(null);
  const [videoDeleteLoading, setVideoDeleteLoading] = useState(false);
  const [videoSearchQuery, setVideoSearchQuery] = useState('');
  const [editStatsVideo, setEditStatsVideo] = useState<DbVideo | null>(null);
  const [editStatsForm, setEditStatsForm] = useState({ views: 0, likes_count: 0 });
  const [editStatsLoading, setEditStatsLoading] = useState(false);
  const [editStatsError, setEditStatsError] = useState('');
  const [editStatsViews, setEditStatsViews] = useState(0);
  const [editStatsLikes, setEditStatsLikes] = useState(0);

  // Edit video state
  const [editVideoTarget, setEditVideoTarget] = useState<DbVideo | null>(null);
  const [editVideoForm, setEditVideoForm] = useState({
    title: '',
    description: '',
    tag: '',
    category: '',
    video_status: '',
    thumbnail: '',
    thumbnail_alt: '',
  });
  const [editVideoLoading, setEditVideoLoading] = useState(false);
  const [editVideoError, setEditVideoError] = useState('');
  const [editThumbnailFile, setEditThumbnailFile] = useState<File | null>(null);
  const [editThumbnailPreview, setEditThumbnailPreview] = useState<string | null>(null);
  const editThumbnailInputRef = useRef<HTMLInputElement>(null);

  // Upload state
  const [uploadForm, setUploadForm] = useState({
    title: '',
    tag: '',
    description: '',
    category: VIDEO_CATEGORIES[0],
    submittedBy: '',
    thumbnail: '',
    thumbnailAlt: '',
  });
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadPreviewUrl, setUploadPreviewUrl] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [uploadSourceType, setUploadSourceType] = useState<UploadSourceType>('file');
  const [videoUrl, setVideoUrl] = useState('');
  const [customThumbnailFile, setCustomThumbnailFile] = useState<File | null>(null);
  const [customThumbnailPreview, setCustomThumbnailPreview] = useState<string | null>(null);
  const thumbnailInputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Admin thumbnail selection state
  const [adminThumbnailOptions, setAdminThumbnailOptions] = useState<{ url: string; label: string }[]>([]);
  const [adminSelectedThumbnail, setAdminSelectedThumbnail] = useState<string>('');
  const [adminThumbnailLoadErrors, setAdminThumbnailLoadErrors] = useState<Record<number, boolean>>({});
  const [adminShowThumbnailPicker, setAdminShowThumbnailPicker] = useState(false);

  // Admins state
  const [adminUsers, setAdminUsers] = useState<UserProfile[]>([]);
  const [adminsLoading, setAdminsLoading] = useState(false);
  const [adminsError, setAdminsError] = useState('');
  const [adminSearchQuery, setAdminSearchQuery] = useState('');
  const [adminSearchResults, setAdminSearchResults] = useState<UserProfile[]>([]);
  const [adminSearchLoading, setAdminSearchLoading] = useState(false);
  // Sign-out-all state
  const [signOutAllLoading, setSignOutAllLoading] = useState(false);
  const [signOutAllResult, setSignOutAllResult] = useState<{ success: boolean; message: string } | null>(null);
  const [adminSearchError, setAdminSearchError] = useState('');
  const [adminToggleLoadingId, setAdminToggleLoadingId] = useState<string | null>(null);

  // Featured video state
  const [featuredVideo, setFeaturedVideo] = useState<DbVideo | null>(null);
  const [featuredLoading, setFeaturedLoading] = useState(false);
  const [featuredError, setFeaturedError] = useState('');
  const [featuredSetLoadingId, setFeaturedSetLoadingId] = useState<string | null>(null);
  const [featuredSearchQuery, setFeaturedSearchQuery] = useState('');

  const supabaseRef = useRef(createClient());
  const supabase = supabaseRef.current;

  // Ads state declarations
  const [ads, setAds] = useState<AdRecord[]>([]);
  const [adsLoading, setAdsLoading] = useState(false);
  const [adsError, setAdsError] = useState('');
  const [showAdForm, setShowAdForm] = useState(false);
  const [adForm, setAdForm] = useState({
    title: '',
    advertiser: '',
    image_url: '',
    image_alt: '',
    link_url: '',
    placement: 'banner',
    notes: '',
  });
  const [adFormError, setAdFormError] = useState('');
  const [adFormLoading, setAdFormLoading] = useState(false);
  const [adImageFile, setAdImageFile] = useState<File | null>(null);
  const [adImagePreview, setAdImagePreview] = useState<string | null>(null);
  const adImageInputRef = useRef<HTMLInputElement>(null);
  const [adToggleLoadingId, setAdToggleLoadingId] = useState<string | null>(null);
  const [adDeleteConfirmId, setAdDeleteConfirmId] = useState<string | null>(null);
  const [adDeleteLoading, setAdDeleteLoading] = useState(false);
const [editingAd, setEditingAd] = useState<AdRecord | null>(null);

  // News state
  const [newsPosts, setNewsPosts] = useState<NewsPost[]>([]);
  const [newsLoading, setNewsLoading] = useState(false);
  const [newsError, setNewsError] = useState('');
  const [showNewsForm, setShowNewsForm] = useState(false);
  const [editingPost, setEditingPost] = useState<NewsPost | null>(null);
  const [newsForm, setNewsForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    cover_image: '',
    cover_image_alt: '',
    category: 'News',
    author: 'ViddStar',
    is_published: false,
    is_featured: false,
    ad_placement: 'bottom',
    ad_id: '',
    scheduled_at: '',
    meta_title: '',
    meta_description: '',
    og_image: '',
  });
  const [newsFormTags, setNewsFormTags] = useState<string[]>([]);
  const [newsFormTagInput, setNewsFormTagInput] = useState('');
  const [newsFormCategories, setNewsFormCategories] = useState<string[]>(['News']);
  const [newsFormError, setNewsFormError] = useState('');
  const [newsFormLoading, setNewsFormLoading] = useState(false);
  const [newsDeleteConfirmId, setNewsDeleteConfirmId] = useState<string | null>(null);
  const [newsDeleteLoading, setNewsDeleteLoading] = useState(false);
  const [newsCoverFile, setNewsCoverFile] = useState<File | null>(null);
  const [newsCoverPreview, setNewsCoverPreview] = useState<string | null>(null);
  const newsCoverInputRef = useRef<HTMLInputElement>(null);
  const [newsGalleryFiles, setNewsGalleryFiles] = useState<File[]>([]);
  const [newsGalleryPreviews, setNewsGalleryPreviews] = useState<string[]>([]);
  const [newsGalleryExisting, setNewsGalleryExisting] = useState<string[]>([]);
  const newsGalleryInputRef = useRef<HTMLInputElement>(null);
  const newsContentRef = useRef<HTMLTextAreaElement>(null);
  const newsInlineImageInputRef = useRef<HTMLInputElement>(null);
  const [newsInlineImageUploading, setNewsInlineImageUploading] = useState(false);

  // Auto-save draft state
  const [autoSaveStatus, setAutoSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle');
  const autoSaveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const autoSaveDraftIdRef = useRef<string | null>(null);

  // Edit news post views state
  const [editNewsViewsPost, setEditNewsViewsPost] = useState<NewsPost | null>(null);
  const [editNewsViewsCount, setEditNewsViewsCount] = useState(0);
  const [editNewsViewsLoading, setEditNewsViewsLoading] = useState(false);
  const [editNewsViewsError, setEditNewsViewsError] = useState('');

  // Insert a markdown link at cursor / around selection
  const handleInsertLink = () => {
    const textarea = newsContentRef.current;
    if (!textarea) return;
    const start = textarea.selectionStart ?? newsForm.content.length;
    const end = textarea.selectionEnd ?? newsForm.content.length;
    const selectedText = newsForm.content.slice(start, end);
    const url = window.prompt('Enter the URL:', 'https://');
    if (!url) return;
    const linkText = selectedText || 'link text';
    const markdown = `[${linkText}](${url})`;
    const before = newsForm.content.slice(0, start);
    const after = newsForm.content.slice(end);
    const newContent = before + markdown + after;
    setNewsForm(prev => ({ ...prev, content: newContent }));
    setTimeout(() => {
      if (newsContentRef.current) {
        const newPos = before.length + markdown.length;
        newsContentRef.current.focus();
        newsContentRef.current.setSelectionRange(newPos, newPos);
      }
    }, 50);
  };

  // Insert a YouTube embed marker at cursor position
  const handleInsertYouTube = () => {
    const textarea = newsContentRef.current;
    const input = window.prompt('Paste a YouTube video URL or Video ID:');
    if (!input) return;
    const trimmed = input.trim();
    // Check if it's already just a video ID (11 chars, alphanumeric + _ -)
    const videoId = /^[a-zA-Z0-9_-]{11}$/.test(trimmed) ? trimmed : extractYouTubeId(trimmed);
    if (!videoId) {
      window.alert('Could not find a valid YouTube video ID in that URL. Please try again.');
      return;
    }
    const marker = `[youtube:${videoId}]`;
    if (textarea) {
      const start = textarea.selectionStart ?? newsForm.content.length;
      const before = newsForm.content.slice(0, start);
      const after = newsForm.content.slice(start);
      const newContent = before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n' + after;
      setNewsForm(prev => ({ ...prev, content: newContent }));
      setTimeout(() => {
        if (newsContentRef.current) {
          const newPos = (before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n').length;
          newsContentRef.current.focus();
          newsContentRef.current.setSelectionRange(newPos, newPos);
        }
      }, 50);
    } else {
      setNewsForm(prev => ({ ...prev, content: prev.content + (prev.content ? '\n' : '') + marker + '\n' }));
    }
  };

  // Insert a Twitter/X embed marker at cursor position
  const handleInsertTwitter = () => {
    const textarea = newsContentRef.current;
    const input = window.prompt('Paste a Twitter/X post URL:\n(e.g. https://twitter.com/user/status/123456789 or https://x.com/user/status/123456789)');
    if (!input) return;
    const trimmed = input.trim();
    // Extract tweet ID from URL
    const tweetMatch = trimmed.match(/(?:twitter\.com|x\.com)\/[^/]+\/status\/(\d+)/);
    if (!tweetMatch) {
      window.alert('Could not find a valid Twitter/X post URL. Please use a URL like:\nhttps://twitter.com/username/status/1234567890');
      return;
    }
    const tweetId = tweetMatch[1];
    const marker = `[twitter:${tweetId}]`;
    if (textarea) {
      const start = textarea.selectionStart ?? newsForm.content.length;
      const before = newsForm.content.slice(0, start);
      const after = newsForm.content.slice(start);
      const newContent = before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n' + after;
      setNewsForm(prev => ({ ...prev, content: newContent }));
      setTimeout(() => {
        if (newsContentRef.current) {
          const newPos = (before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n').length;
          newsContentRef.current.focus();
          newsContentRef.current.setSelectionRange(newPos, newPos);
        }
      }, 50);
    } else {
      setNewsForm(prev => ({ ...prev, content: prev.content + (prev.content ? '\n' : '') + marker + '\n' }));
    }
  };

  // Insert an Instagram embed marker at cursor position
  const handleInsertInstagram = () => {
    const textarea = newsContentRef.current;
    const input = window.prompt('Paste an Instagram post URL:\n(e.g. https://www.instagram.com/p/ABC123/ or https://www.instagram.com/reel/ABC123/)');
    if (!input) return;
    const trimmed = input.trim();
    // Extract Instagram shortcode from URL
    const igMatch = trimmed.match(/instagram\.com\/(?:p|reel|tv)\/([A-Za-z0-9_-]+)/);
    if (!igMatch) {
      window.alert('Could not find a valid Instagram post URL. Please use a URL like:\nhttps://www.instagram.com/p/ABC123xyz/');
      return;
    }
    const shortcode = igMatch[1];
    const marker = `[instagram:${shortcode}]`;
    if (textarea) {
      const start = textarea.selectionStart ?? newsForm.content.length;
      const before = newsForm.content.slice(0, start);
      const after = newsForm.content.slice(start);
      const newContent = before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n' + after;
      setNewsForm(prev => ({ ...prev, content: newContent }));
      setTimeout(() => {
        if (newsContentRef.current) {
          const newPos = (before + (before && !before.endsWith('\n') ? '\n' : '') + marker + '\n').length;
          newsContentRef.current.focus();
          newsContentRef.current.setSelectionRange(newPos, newPos);
        }
      }, 50);
    } else {
      setNewsForm(prev => ({ ...prev, content: prev.content + (prev.content ? '\n' : '') + marker + '\n' }));
    }
  };

  // Advertisers state
  const [advertisers, setAdvertisers] = useState<Advertiser[]>([]);
  const [advertisersLoading, setAdvertisersLoading] = useState(false);
  const [advertisersError, setAdvertisersError] = useState('');
  const [showAdvertiserForm, setShowAdvertiserForm] = useState(false);
  const [editingAdvertiser, setEditingAdvertiser] = useState<Advertiser | null>(null);
  const [advertiserForm, setAdvertiserForm] = useState({
    company_name: '', contact_name: '', email: '', phone: '', website: '', ad_budget: '', notes: '',
  });
  const [advertiserFormError, setAdvertiserFormError] = useState('');
  const [advertiserFormLoading, setAdvertiserFormLoading] = useState(false);
  const [advertiserDeleteConfirmId, setAdvertiserDeleteConfirmId] = useState<string | null>(null);
  const [advertiserDeleteLoading, setAdvertiserDeleteLoading] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [rejectingId, setRejectingId] = useState<string | null>(null);
  const [approvalActionLoading, setApprovalActionLoading] = useState<string | null>(null);

  // Revenue settlements state
  const [settlements, setSettlements] = useState<RevenueSettlement[]>([]);
  const [settlementsLoading, setSettlementsLoading] = useState(false);
  const [settlementsError, setSettlementsError] = useState('');
  const [showSettlementForm, setShowSettlementForm] = useState(false);
  const [settlementForm, setSettlementForm] = useState({
    advertiser_id: '', period_start: '', period_end: '', total_impressions: '', total_clicks: '',
    amount_due: '', amount_paid: '', payment_status: 'pending', invoice_number: '', notes: '',
  });
  const [settlementFormError, setSettlementFormError] = useState('');
  const [settlementFormLoading, setSettlementFormLoading] = useState(false);
  const [settlementDeleteConfirmId, setSettlementDeleteConfirmId] = useState<string | null>(null);
  const [settlementDeleteLoading, setSettlementDeleteLoading] = useState(false);
  const [settlementMarkPaidId, setSettlementMarkPaidId] = useState<string | null>(null);

  // Ad payments (Stripe) state
  const [adPayments, setAdPayments] = useState<AdPaymentRecord[]>([]);
  const [adPaymentsLoading, setAdPaymentsLoading] = useState(false);
  const [adPaymentsError, setAdPaymentsError] = useState('');

  // Flagged videos / reports state
  const [reports, setReports] = useState<VideoReport[]>([]);
  const [reportsLoading, setReportsLoading] = useState(false);
  const [reportsError, setReportsError] = useState('');
  const [reportsFilter, setReportsFilter] = useState<'all' | 'pending' | 'reviewed' | 'dismissed' | 'removed'>('pending');
  const [selectedReportIds, setSelectedReportIds] = useState<Set<string>>(new Set());
  const [reportActionLoading, setReportActionLoading] = useState<string | null>(null);
  const [bulkActionLoading, setBulkActionLoading] = useState(false);
  const [expandedReportId, setExpandedReportId] = useState<string | null>(null);
  const [reportAdminNotes, setReportAdminNotes] = useState<Record<string, string>>({});
  const [suspendConfirmId, setSuspendConfirmId] = useState<string | null>(null);

  // Admin add-comment state
  const [adminCommentTarget, setAdminCommentTarget] = useState<'video' | 'blog'>('video');
  const [adminCommentVideoId, setAdminCommentVideoId] = useState('');
  const [adminCommentPostId, setAdminCommentPostId] = useState('');
  const [adminCommentAuthor, setAdminCommentAuthor] = useState('ViddStar Admin');
  const [adminCommentBody, setAdminCommentBody] = useState('');
  const [adminCommentLoading, setAdminCommentLoading] = useState(false);
  const [adminCommentError, setAdminCommentError] = useState('');
  const [adminCommentSuccess, setAdminCommentSuccess] = useState('');

  const fetchUsers = useCallback(async () => {
    setUsersLoading(true);
    setUsersError('');
    try {
      const { data, error } = await supabase.from('user_profiles').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setUsers(data || []);
    } catch (err: any) {
      setUsersError(err.message || 'Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  }, [supabase]);

  const handleSignOutAllUsers = async () => {
    if (!window.confirm('Are you sure you want to sign out ALL users? This will invalidate every active session immediately.')) return;
    setSignOutAllLoading(true);
    setSignOutAllResult(null);
    try {
      const res = await fetch('/api/admin/signout-all', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const json = await res.json();
      if (json.success) {
        setSignOutAllResult({ success: true, message: `Successfully signed out ${json.signedOut} user${json.signedOut !== 1 ? 's' : ''}.` });
      } else {
        setSignOutAllResult({ success: false, message: json.error || 'Failed to sign out users.' });
      }
    } catch (err: any) {
      setSignOutAllResult({ success: false, message: err.message || 'Unexpected error.' });
    } finally {
      setSignOutAllLoading(false);
    }
  };

  const fetchAllVideos = useCallback(async () => {
    setVideosLoading(true);
    setVideosError('');
    try {
      const { data, error } = await supabase.from('videos').select('*').order('submitted_at', { ascending: false });
      if (error) throw error;
      setAllVideos(data || []);
    } catch (err: any) {
      setVideosError(err.message || 'Failed to load videos');
    } finally {
      setVideosLoading(false);
    }
  }, [supabase]);

  const fetchAdmins = useCallback(async () => {
    setAdminsLoading(true);
    setAdminsError('');
    try {
      const { data, error } = await supabase.from('user_profiles').select('*').eq('is_admin', true).order('created_at', { ascending: false });
      if (error) throw error;
      setAdminUsers(data || []);
    } catch (err: any) {
      setAdminsError(err.message || 'Failed to load admins');
    } finally {
      setAdminsLoading(false);
    }
  }, [supabase]);

  const fetchFeaturedVideo = useCallback(async () => {
    setFeaturedLoading(true);
    setFeaturedError('');
    try {
      const { data, error } = await supabase
        .from('videos')
        .select('*')
        .eq('is_featured', true)
        .limit(1);
      if (error) throw error;
      setFeaturedVideo(data && data.length > 0 ? data[0] : null);
    } catch (err: any) {
      setFeaturedError(err.message || 'Failed to load featured video');
    } finally {
      setFeaturedLoading(false);
    }
  }, [supabase]);

  const fetchAds = useCallback(async () => {
    setAdsLoading(true);
    setAdsError('');
    try {
      const { data, error } = await supabase.from('ads').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setAds(data || []);
    } catch (err: any) {
      setAdsError(err.message || 'Failed to load ads');
    } finally {
      setAdsLoading(false);
    }
  }, [supabase]);

  const fetchNewsPosts = useCallback(async () => {
    setNewsLoading(true);
    setNewsError('');
    try {
      const { data, error } = await supabase.from('news_posts').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setNewsPosts(data || []);
    } catch (err: any) {
      setNewsError(err.message || 'Failed to load news posts');
    } finally {
      setNewsLoading(false);
    }
  }, [supabase]);

  const fetchAdPayments = useCallback(async () => {
    setAdPaymentsLoading(true);
    setAdPaymentsError('');
    try {
      const { data, error } = await supabase
        .from('advertiser_payments')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAdPayments(data || []);
    } catch (err: any) {
      setAdPaymentsError(err.message || 'Failed to load ad payments');
    } finally {
      setAdPaymentsLoading(false);
    }
  }, [supabase]);

  const fetchReports = useCallback(async () => {
    setReportsLoading(true);
    setReportsError('');
    try {
      const { data: reportData, error } = await supabase
        .from('video_reports')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;

      // Enrich with video info
      const videoIds = [...new Set((reportData || []).map((r: any) => r.video_id))];
      let videoMap: Record<string, { title: string; thumbnail?: string; submitted_by?: string }> = {};
      if (videoIds.length > 0) {
        const { data: videos } = await supabase
          .from('videos')
          .select('id, title, thumbnail, submitted_by')
          .in('id', videoIds);
        (videos || []).forEach((v: any) => {
          videoMap[v.id] = { title: v.title, thumbnail: v.thumbnail, submitted_by: v.submitted_by };
        });
      }

      const enriched: VideoReport[] = (reportData || []).map((r: any) => ({
        ...r,
        video_title: videoMap[r.video_id]?.title || 'Unknown Video',
        video_thumbnail: videoMap[r.video_id]?.thumbnail,
        video_submitted_by: videoMap[r.video_id]?.submitted_by,
      }));
      setReports(enriched);
    } catch (err: any) {
      setReportsError(err.message || 'Failed to load reports');
    } finally {
      setReportsLoading(false);
    }
  }, [supabase]);

  const handleReportAction = async (
    report: VideoReport,
    action: 'approve' | 'remove' | 'dismiss' | 'suspend'
  ) => {
    setReportActionLoading(report.id);
    try {
      const notes = reportAdminNotes[report.id] || '';

      if (action === 'approve') {
        // Mark report as reviewed, video stays
        await supabase.from('video_reports').update({
          status: 'reviewed',
          action_taken: 'approved',
          admin_notes: notes || null,
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).eq('id', report.id);
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'reviewed', action_taken: 'approved' } : r));
        showSuccess('Report reviewed — video approved to stay.');
      } else if (action === 'remove') {
        // Remove the video and mark report as removed
        await supabase.from('videos').delete().eq('id', report.video_id);
        await supabase.from('video_reports').update({
          status: 'removed',
          action_taken: 'video_removed',
          admin_notes: notes || null,
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).eq('id', report.id);
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'removed', action_taken: 'video_removed' } : r));
        showSuccess('Video removed successfully.');
      } else if (action === 'dismiss') {
        await supabase.from('video_reports').update({
          status: 'dismissed',
          action_taken: 'dismissed',
          admin_notes: notes || null,
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).eq('id', report.id);
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'dismissed', action_taken: 'dismissed' } : r));
        showSuccess('Report dismissed.');
      } else if (action === 'suspend') {
        // Suspend creator by submitted_by name — find user profile
        if (report.video_submitted_by) {
          await supabase
            .from('user_profiles')
            .update({ is_suspended: true })
            .or(`username.eq.${report.video_submitted_by},full_name.eq.${report.video_submitted_by}`);
        }
        await supabase.from('video_reports').update({
          status: 'reviewed',
          action_taken: 'creator_suspended',
          admin_notes: notes || null,
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).eq('id', report.id);
        setReports(prev => prev.map(r => r.id === report.id ? { ...r, status: 'reviewed', action_taken: 'creator_suspended' } : r));
        setSuspendConfirmId(null);
        showSuccess('Creator suspended.');
      }
    } catch (err: any) {
      setReportsError(err.message || 'Action failed');
    } finally {
      setReportActionLoading(null);
    }
  };

  const handleBulkReportAction = async (action: 'dismiss' | 'remove') => {
    if (selectedReportIds.size === 0) return;
    setBulkActionLoading(true);
    try {
      const ids = Array.from(selectedReportIds);
      const targetReports = reports.filter(r => ids.includes(r.id));

      if (action === 'remove') {
        const videoIds = [...new Set(targetReports.map(r => r.video_id))];
        for (const vid of videoIds) {
          await supabase.from('videos').delete().eq('id', vid);
        }
        await supabase.from('video_reports').update({
          status: 'removed',
          action_taken: 'video_removed',
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).in('id', ids);
        setReports(prev => prev.map(r => ids.includes(r.id) ? { ...r, status: 'removed', action_taken: 'video_removed' } : r));
        showSuccess(`${ids.length} video(s) removed.`);
      } else {
        await supabase.from('video_reports').update({
          status: 'dismissed',
          action_taken: 'dismissed',
          resolved_at: new Date().toISOString(),
          resolved_by: 'Admin',
        }).in('id', ids);
        setReports(prev => prev.map(r => ids.includes(r.id) ? { ...r, status: 'dismissed', action_taken: 'dismissed' } : r));
        showSuccess(`${ids.length} report(s) dismissed.`);
      }
      setSelectedReportIds(new Set());
    } catch (err: any) {
      setReportsError(err.message || 'Bulk action failed');
    } finally {
      setBulkActionLoading(false);
    }
  };

  const handleAdminPostComment = async () => {
    const targetId = adminCommentTarget === 'video' ? adminCommentVideoId.trim() : adminCommentPostId.trim();
    if (!targetId) {
      setAdminCommentError(adminCommentTarget === 'video' ? 'Please enter a Video ID.' : 'Please select a blog post.');
      return;
    }
    if (!adminCommentBody.trim()) {
      setAdminCommentError('Comment text is required.');
      return;
    }
    setAdminCommentLoading(true);
    setAdminCommentError('');
    setAdminCommentSuccess('');
    try {
      const payload: Record<string, unknown> = {
        author: adminCommentAuthor.trim() || 'ViddStar Admin',
        body: adminCommentBody.trim(),
        moderation_status: 'approved',
        spam_score: 0,
      };
      if (adminCommentTarget === 'video') {
        payload.video_id = targetId;
      } else {
        payload.video_id = targetId;
        payload.news_post_id = targetId;
      }
      const { error } = await supabase.from('comments').insert(payload);
      if (error) throw error;
      setAdminCommentBody('');
      setAdminCommentVideoId('');
      setAdminCommentPostId('');
      setAdminCommentSuccess('Comment posted and auto-approved!');
      setTimeout(() => setAdminCommentSuccess(''), 4000);
    } catch (e: any) {
      setAdminCommentError(e.message || 'Failed to post comment.');
    } finally {
      setAdminCommentLoading(false);
    }
  };

  // Auto-save draft when form content changes (debounced 3 seconds)
  useEffect(() => {
    // Only auto-save when the news form is open and has a title
    if (!showNewsForm || !newsForm.title.trim()) return;

    // Clear any pending auto-save timer
    if (autoSaveTimerRef.current) {
      clearTimeout(autoSaveTimerRef.current);
    }

    // Set status to indicate a save is pending
    setAutoSaveStatus('idle');

    autoSaveTimerRef.current = setTimeout(async () => {
      setAutoSaveStatus('saving');
      try {
        const existingId = editingPost?.id || autoSaveDraftIdRef.current || undefined;
        const slug = newsForm.slug.trim() || await generateUniqueSlug(newsForm.title.trim(), existingId);
        const primaryCategory = newsFormCategories[0] || newsForm.category.trim() || 'News';
        const allCategories = newsFormCategories.length > 0 ? newsFormCategories : [newsForm.category.trim() || 'News'];

        const payload = {
          title: newsForm.title.trim(),
          slug,
          excerpt: newsForm.excerpt.trim(),
          content: newsForm.content.trim(),
          cover_image: newsForm.cover_image.trim(),
          cover_image_alt: newsForm.cover_image_alt.trim() || newsForm.title.trim(),
          category: primaryCategory,
          categories: allCategories,
          tags: newsFormTags,
          author: newsForm.author.trim() || 'ViddStar',
          is_published: false,
          is_featured: newsForm.is_featured,
          ad_placement: newsForm.ad_placement || 'bottom',
          ad_id: newsForm.ad_id || null,
          scheduled_at: null,
          meta_title: newsForm.meta_title.trim() || null,
          meta_description: newsForm.meta_description.trim() || null,
          og_image: newsForm.og_image.trim() || null,
        };

        if (editingPost) {
          // Update existing post as draft
          const { error } = await supabase.from('news_posts').update(payload).eq('id', editingPost.id);
          if (error) throw error;
        } else if (autoSaveDraftIdRef.current) {
          // Update the previously auto-saved draft
          const { error } = await supabase.from('news_posts').update(payload).eq('id', autoSaveDraftIdRef.current);
          if (error) throw error;
        } else {
          // Create a new draft for the first time
          const newId = crypto.randomUUID();
          const { error } = await supabase.from('news_posts').insert({
            id: newId,
            ...payload,
            views: 0,
          });
          if (error) throw error;
          autoSaveDraftIdRef.current = newId;
        }
        setAutoSaveStatus('saved');
        // Reset to idle after 3 seconds
        setTimeout(() => setAutoSaveStatus('idle'), 3000);
      } catch {
        setAutoSaveStatus('error');
        setTimeout(() => setAutoSaveStatus('idle'), 4000);
      }
    }, 3000);

    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [newsForm, newsFormTags, newsFormCategories, showNewsForm]);

  // Reset auto-save draft ID when form is closed or a new form is opened
  useEffect(() => {
    if (!showNewsForm) {
      autoSaveDraftIdRef.current = null;
      setAutoSaveStatus('idle');
    }
  }, [showNewsForm]);

  const generateSlug = (title: string): string => {
    return title
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, '')
      .replace(/[\s_-]+/g, '-')
      .replace(/^-+|-+$/g, '');
  };

  const generateUniqueSlug = async (title: string, excludeId?: string): Promise<string> => {
    const base = generateSlug(title);
    let candidate = base;
    let counter = 1;
    while (true) {
      let query = supabase
        .from('news_posts')
        .select('id')
        .eq('slug', candidate);
      if (excludeId) {
        query = query.neq('id', excludeId);
      }
      const { data } = await query.limit(1);
      if (!data || data.length === 0) break;
      candidate = `${base}-${counter}`;
      counter++;
    }
    return candidate;
  };

  const handleNewsCoverChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewsCoverFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setNewsCoverPreview(url);
    } else {
      setNewsCoverPreview(null);
    }
  };

  const openNewsForm = (post?: NewsPost) => {
    if (post) {
      setEditingPost(post);
      setNewsForm({
        title: post.title || '',
        slug: post.slug || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        cover_image: post.cover_image || '',
        cover_image_alt: post.cover_image_alt || '',
        category: post.category || 'News',
        author: post.author || 'ViddStar',
        is_published: post.is_published ?? false,
        is_featured: post.is_featured ?? false,
        ad_placement: (post as any).ad_placement || 'bottom',
        ad_id: (post as any).ad_id || '',
        scheduled_at: post.scheduled_at ? post.scheduled_at.slice(0, 16) : '',
        meta_title: post.meta_title || '',
        meta_description: post.meta_description || '',
        og_image: post.og_image || '',
      });
      setNewsFormTags(Array.isArray(post.tags) ? post.tags : []);
      setNewsFormCategories(Array.isArray(post.categories) && post.categories.length > 0 ? post.categories : [post.category || 'News']);
      setNewsGalleryExisting(Array.isArray((post as any).gallery_images) ? (post as any).gallery_images : []);
    } else {
      setEditingPost(null);
      setNewsForm({
        title: '',
        slug: '',
        excerpt: '',
        content: '',
        cover_image: '',
        cover_image_alt: '',
        category: 'News',
        author: 'ViddStar',
        is_published: false,
        is_featured: false,
        ad_placement: 'bottom',
        ad_id: '',
        scheduled_at: '',
        meta_title: '',
        meta_description: '',
        og_image: '',
      });
      setNewsFormTags([]);
      setNewsFormTagInput('');
      setNewsFormCategories(['News']);
      setNewsGalleryExisting([]);
    }
    setNewsCoverFile(null);
    setNewsCoverPreview(null);
    if (newsCoverInputRef.current) newsCoverInputRef.current.value = '';
    setNewsGalleryFiles([]);
    setNewsGalleryPreviews([]);
    if (newsGalleryInputRef.current) newsGalleryInputRef.current.value = '';
    setNewsFormError('');
    setShowNewsForm(true);
  };

  const handleSaveNewsPost = async () => {
    if (!newsForm.title.trim()) { setNewsFormError('Title is required.'); return; }
    setNewsFormLoading(true);
    setNewsFormError('');
    try {
      let coverImageUrl = newsForm.cover_image.trim();
      if (newsCoverFile) {
        const fileName = `news/${crypto.randomUUID()}-${newsCoverFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('news')
          .upload(fileName, newsCoverFile, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('news').getPublicUrl(fileName);
        coverImageUrl = urlData.publicUrl;
      }

      // Upload new gallery images
      const uploadedGalleryUrls: string[] = [];
      for (const file of newsGalleryFiles) {
        const fileName = `news/gallery/${crypto.randomUUID()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('news')
          .upload(fileName, file, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('news').getPublicUrl(fileName);
        uploadedGalleryUrls.push(urlData.publicUrl);
      }
      const finalGalleryImages = [...newsGalleryExisting, ...uploadedGalleryUrls];

      const slug = newsForm.slug.trim() || await generateUniqueSlug(newsForm.title.trim(), editingPost?.id);
      const primaryCategory = newsFormCategories[0] || newsForm.category.trim() || 'News';
      const allCategories = newsFormCategories.length > 0 ? newsFormCategories : [newsForm.category.trim() || 'News'];
      if (editingPost) {
        const { error } = await supabase.from('news_posts').update({
          title: newsForm.title.trim(),
          slug,
          excerpt: newsForm.excerpt.trim(),
          content: newsForm.content.trim(),
          cover_image: coverImageUrl,
          cover_image_alt: newsForm.cover_image_alt.trim() || newsForm.title.trim(),
          category: primaryCategory,
          categories: allCategories,
          tags: newsFormTags,
          author: newsForm.author.trim() || 'ViddStar',
          is_published: newsForm.scheduled_at ? true : newsForm.is_published,
          is_featured: newsForm.is_featured,
          gallery_images: finalGalleryImages,
          ad_placement: newsForm.ad_placement || 'bottom',
          ad_id: newsForm.ad_id || null,
          scheduled_at: newsForm.scheduled_at ? new Date(newsForm.scheduled_at).toISOString() : null,
          meta_title: newsForm.meta_title.trim() || null,
          meta_description: newsForm.meta_description.trim() || null,
          og_image: newsForm.og_image.trim() || null,
        }).eq('id', editingPost.id);
        if (error) throw error;
        showSuccess('Post updated successfully!');
      } else {
        const { error } = await supabase.from('news_posts').insert({
          id: crypto.randomUUID(),
          title: newsForm.title.trim(),
          slug,
          excerpt: newsForm.excerpt.trim(),
          content: newsForm.content.trim(),
          cover_image: coverImageUrl,
          cover_image_alt: newsForm.cover_image_alt.trim() || newsForm.title.trim(),
          category: primaryCategory,
          categories: allCategories,
          tags: newsFormTags,
          author: newsForm.author.trim() || 'ViddStar',
          is_published: newsForm.scheduled_at ? true : newsForm.is_published,
          is_featured: newsForm.is_featured,
          gallery_images: finalGalleryImages,
          ad_placement: newsForm.ad_placement || 'bottom',
          ad_id: newsForm.ad_id || null,
          scheduled_at: newsForm.scheduled_at ? new Date(newsForm.scheduled_at).toISOString() : null,
          meta_title: newsForm.meta_title.trim() || null,
          meta_description: newsForm.meta_description.trim() || null,
          og_image: newsForm.og_image.trim() || null,
          views: 0,
        });
        if (error) throw error;
        showSuccess(newsForm.scheduled_at ? 'Post scheduled successfully!' : 'Post published successfully!');
      }
      setShowNewsForm(false);
      setEditingPost(null);
      fetchNewsPosts();
    } catch (err: any) {
      setNewsFormError(err.message || 'Failed to save post');
    } finally {
      setNewsFormLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    if (!newsForm.title.trim()) { setNewsFormError('Title is required.'); return; }
    setNewsFormLoading(true);
    setNewsFormError('');
    try {
      let coverImageUrl = newsForm.cover_image.trim();
      if (newsCoverFile) {
        const fileName = `news/${crypto.randomUUID()}-${newsCoverFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('news')
          .upload(fileName, newsCoverFile, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('news').getPublicUrl(fileName);
        coverImageUrl = urlData.publicUrl;
      }

      const uploadedGalleryUrls: string[] = [];
      for (const file of newsGalleryFiles) {
        const fileName = `news/gallery/${crypto.randomUUID()}-${file.name}`;
        const { error: uploadError } = await supabase.storage
          .from('news')
          .upload(fileName, file, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('news').getPublicUrl(fileName);
        uploadedGalleryUrls.push(urlData.publicUrl);
      }
      const finalGalleryImages = [...newsGalleryExisting, ...uploadedGalleryUrls];

      const slug = newsForm.slug.trim() || await generateUniqueSlug(newsForm.title.trim(), editingPost?.id);
      const primaryCategoryDraft = newsFormCategories[0] || newsForm.category.trim() || 'News';
      const allCategoriesDraft = newsFormCategories.length > 0 ? newsFormCategories : [newsForm.category.trim() || 'News'];
      if (editingPost) {
        const { error } = await supabase.from('news_posts').update({
          title: newsForm.title.trim(),
          slug,
          excerpt: newsForm.excerpt.trim(),
          content: newsForm.content.trim(),
          cover_image: coverImageUrl,
          cover_image_alt: newsForm.cover_image_alt.trim() || newsForm.title.trim(),
          category: primaryCategoryDraft,
          categories: allCategoriesDraft,
          tags: newsFormTags,
          author: newsForm.author.trim() || 'ViddStar',
          is_published: false,
          is_featured: newsForm.is_featured,
          gallery_images: finalGalleryImages,
          ad_placement: newsForm.ad_placement || 'bottom',
          ad_id: newsForm.ad_id || null,
          scheduled_at: null,
          meta_title: newsForm.meta_title.trim() || null,
          meta_description: newsForm.meta_description.trim() || null,
          og_image: newsForm.og_image.trim() || null,
        }).eq('id', editingPost.id);
        if (error) throw error;
        showSuccess('Draft saved successfully!');
      } else {
        const { error } = await supabase.from('news_posts').insert({
          id: crypto.randomUUID(),
          title: newsForm.title.trim(),
          slug,
          excerpt: newsForm.excerpt.trim(),
          content: newsForm.content.trim(),
          cover_image: coverImageUrl,
          cover_image_alt: newsForm.cover_image_alt.trim() || newsForm.title.trim(),
          category: primaryCategoryDraft,
          categories: allCategoriesDraft,
          tags: newsFormTags,
          author: newsForm.author.trim() || 'ViddStar',
          is_published: false,
          is_featured: newsForm.is_featured,
          gallery_images: finalGalleryImages,
          ad_placement: newsForm.ad_placement || 'bottom',
          ad_id: newsForm.ad_id || null,
          scheduled_at: null,
          meta_title: newsForm.meta_title.trim() || null,
          meta_description: newsForm.meta_description.trim() || null,
          og_image: newsForm.og_image.trim() || null,
          views: 0,
        });
        if (error) throw error;
        showSuccess('Draft saved successfully!');
      }
      setShowNewsForm(false);
      setEditingPost(null);
      fetchNewsPosts();
    } catch (err: any) {
      setNewsFormError(err.message || 'Failed to save draft');
    } finally {
      setNewsFormLoading(false);
    }
  };

  const handleNewsInlineImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setNewsInlineImageUploading(true);
    try {
      const fileName = `news/inline/${crypto.randomUUID()}-${file.name}`;
      const { error: uploadErr } = await supabase.storage
        .from('news')
        .upload(fileName, file, { upsert: false });
      if (uploadErr) throw uploadErr;
      const { data: urlData } = supabase.storage.from('news').getPublicUrl(fileName);
      let imageUrl = urlData.publicUrl;
      const altText = file.name.replace(/\.[^/.]+$/, '').replace(/[-_]/g, ' ');
      const markdown = `![${altText}](${imageUrl})`;

      // Insert at cursor position in the textarea
      const textarea = newsContentRef.current;
      if (textarea) {
        const start = textarea.selectionStart ?? newsForm.content.length;
        const end = textarea.selectionEnd ?? newsForm.content.length;
        const before = newsForm.content.slice(0, start);
        const after = newsForm.content.slice(end);
        const newContent = before + (before && !before.endsWith('\n') ? '\n' : '') + markdown + '\n' + after;
        setNewsForm(prev => ({ ...prev, content: newContent }));
        // Restore focus and cursor after React re-render
        setTimeout(() => {
          if (newsContentRef.current) {
            const newPos = (before + (before && !before.endsWith('\n') ? '\n' : '') + markdown + '\n').length;
            newsContentRef.current.focus();
            newsContentRef.current.setSelectionRange(newPos, newPos);
          }
        }, 50);
      } else {
        setNewsForm(prev => ({ ...prev, content: prev.content + (prev.content ? '\n' : '') + markdown + '\n' }));
      }
    } catch (err: any) {
      setNewsFormError('Image upload failed: ' + (err?.message || 'Unknown error'));
    } finally {
      setNewsInlineImageUploading(false);
      if (newsInlineImageInputRef.current) newsInlineImageInputRef.current.value = '';
    }
  };

  const handleToggleNewsPublished = async (post: NewsPost) => {
    try {
      const { error } = await supabase.from('news_posts').update({ is_published: !post.is_published }).eq('id', post.id);
      if (error) throw error;
      setNewsPosts(prev => prev.map(p => p.id === post.id ? { ...p, is_published: !post.is_published } : p));
    } catch (err: any) {
      setNewsError(err.message || 'Failed to toggle post');
    }
  };

  const handleDeleteNewsPost = async (postId: string) => {
    setNewsDeleteLoading(true);
    try {
      const { error } = await supabase.from('news_posts').delete().eq('id', postId);
      if (error) throw error;
      setNewsPosts(prev => prev.filter(p => p.id !== postId));
      setNewsDeleteConfirmId(null);
      showSuccess('Post deleted successfully');
    } catch (err: any) {
      setNewsError(err.message || 'Failed to delete post');
    } finally {
      setNewsDeleteLoading(false);
    }
  };

  const handleAdImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setAdImageFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setAdImagePreview(url);
    } else {
      setAdImagePreview(null);
    }
  };

  const openAdForm = (ad?: AdRecord) => {
    if (ad) {
      setEditingAd(ad);
      setAdForm({
        title: ad.title,
        advertiser: ad.advertiser || '',
        image_url: ad.image_url || '',
        image_alt: ad.image_alt || '',
        link_url: ad.link_url || '',
        placement: ad.placement || 'banner',
        notes: ad.notes || '',
      });
      setAdImageFile(null);
      setAdImagePreview(null);
      if (adImageInputRef.current) adImageInputRef.current.value = '';
    } else {
      setEditingAd(null);
      setAdForm({ title: '', advertiser: '', image_url: '', image_alt: '', link_url: '', placement: 'banner', notes: '' });
      setAdImageFile(null);
      setAdImagePreview(null);
      if (adImageInputRef.current) adImageInputRef.current.value = '';
    }
    setAdFormError('');
    setShowAdForm(true);
  };

  const handleSaveAd = async () => {
    if (!adForm.title.trim()) { setAdFormError('Ad title is required.'); return; }
    if (!adForm.link_url.trim()) { setAdFormError('Destination URL is required.'); return; }
    if (!editingAd && !adForm.image_url.trim() && !adImageFile) { setAdFormError('An ad image or image URL is required.'); return; }
    setAdFormLoading(true);
    setAdFormError('');
    try {
      let imageUrl = adForm.image_url.trim();
      if (adImageFile) {
        const fileName = `ads/${crypto.randomUUID()}-${adImageFile.name}`;
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from('ads')
          .upload(fileName, adImageFile, { upsert: false });
        if (uploadError) throw uploadError;
        const { data: urlData } = supabase.storage.from('ads').getPublicUrl(fileName);
        imageUrl = urlData.publicUrl;
      }
      const payload = {
        title: adForm.title.trim(),
        advertiser: adForm.advertiser.trim(),
        image_url: imageUrl || (editingAd?.image_url ?? ''),
        image_alt: adForm.image_alt.trim() || adForm.title.trim(),
        link_url: adForm.link_url.trim(),
        placement: adForm.placement,
        notes: adForm.notes.trim(),
      };
      if (editingAd) {
        const { error } = await supabase.from('ads').update(payload).eq('id', editingAd.id);
        if (error) throw error;
        showSuccess('Ad updated successfully!');
      } else {
        const { error } = await supabase.from('ads').insert({ id: crypto.randomUUID(), ...payload, is_active: true });
        if (error) throw error;
        showSuccess('Ad saved and activated!');
      }
      setAdForm({ title: '', advertiser: '', image_url: '', image_alt: '', link_url: '', placement: 'banner', notes: '' });
      setAdImageFile(null);
      setAdImagePreview(null);
      if (adImageInputRef.current) adImageInputRef.current.value = '';
      setShowAdForm(false);
      setEditingAd(null);
      fetchAds();
    } catch (err: any) {
      setAdFormError(err.message || 'Failed to save ad');
    } finally {
      setAdFormLoading(false);
    }
  };

  const handleToggleAd = async (ad: AdRecord) => {
    setAdToggleLoadingId(ad.id);
    try {
      const { error } = await supabase.from('ads').update({ is_active: !ad.is_active }).eq('id', ad.id);
      if (error) throw error;
      setAds(prev => prev.map(a => a.id === ad.id ? { ...a, is_active: !ad.is_active } : a));
    } catch (err: any) {
      setAdsError(err.message || 'Failed to toggle ad');
    } finally {
      setAdToggleLoadingId(null);
    }
  };

  const handleDeleteAd = async (adId: string) => {
    setAdDeleteLoading(true);
    try {
      const { error } = await supabase.from('ads').delete().eq('id', adId);
      if (error) throw error;
      setAds(prev => prev.filter(a => a.id !== adId));
      setAdDeleteConfirmId(null);
      showSuccess('Ad deleted successfully');
    } catch (err: any) {
      setAdsError(err.message || 'Failed to delete ad');
    } finally {
      setAdDeleteLoading(false);
    }
  };

  // ── Advertiser handlers ──────────────────────────────────────────────────────
  const fetchAdvertisers = useCallback(async () => {
    setAdvertisersLoading(true);
    setAdvertisersError('');
    try {
      const { data, error } = await supabase.from('advertisers').select('*').order('created_at', { ascending: false });
      if (error) throw error;
      setAdvertisers(data || []);
    } catch (err: any) {
      setAdvertisersError(err.message || 'Failed to load advertisers');
    } finally {
      setAdvertisersLoading(false);
    }
  }, [supabase]);

  const openAdvertiserForm = (adv?: Advertiser) => {
    if (adv) {
      setEditingAdvertiser(adv);
      setAdvertiserForm({
        company_name: adv.company_name || '',
        contact_name: adv.contact_name || '',
        email: adv.email || '',
        phone: adv.phone || '',
        website: adv.website || '',
        ad_budget: adv.ad_budget?.toString() || '',
        notes: adv.notes || '',
      });
    } else {
      setEditingAdvertiser(null);
      setAdvertiserForm({ company_name: '', contact_name: '', email: '', phone: '', website: '', ad_budget: '', notes: '' });
    }
    setAdvertiserFormError('');
    setShowAdvertiserForm(true);
  };

  const handleSaveAdvertiser = async () => {
    if (!advertiserForm.company_name.trim()) { setAdvertiserFormError('Company name is required.'); return; }
    if (!advertiserForm.email.trim()) { setAdvertiserFormError('Email is required.'); return; }
    setAdvertiserFormLoading(true);
    setAdvertiserFormError('');
    try {
      const payload = {
        company_name: advertiserForm.company_name.trim(),
        contact_name: advertiserForm.contact_name.trim(),
        email: advertiserForm.email.trim(),
        phone: advertiserForm.phone.trim() || null,
        website: advertiserForm.website.trim() || null,
        ad_budget: advertiserForm.ad_budget ? parseFloat(advertiserForm.ad_budget) : 0,
        notes: advertiserForm.notes.trim() || null,
        updated_at: new Date().toISOString(),
      };
      if (editingAdvertiser) {
        const { error } = await supabase.from('advertisers').update(payload).eq('id', editingAdvertiser.id);
        if (error) throw error;
        showSuccess('Advertiser updated successfully!');
      } else {
        const { error } = await supabase.from('advertisers').insert({ id: crypto.randomUUID(), ...payload, account_status: 'pending' });
        if (error) throw error;
        showSuccess('Advertiser account created!');
      }
      setShowAdvertiserForm(false);
      setEditingAdvertiser(null);
      fetchAdvertisers();
    } catch (err: any) {
      setAdvertiserFormError(err.message || 'Failed to save advertiser');
    } finally {
      setAdvertiserFormLoading(false);
    }
  };

  const handleApproveAdvertiser = async (adv: Advertiser) => {
    setApprovalActionLoading(adv.id);
    try {
      const { error } = await supabase.from('advertisers').update({
        account_status: 'approved',
        approved_at: new Date().toISOString(),
        approved_by: 'Admin',
        rejection_reason: null,
        updated_at: new Date().toISOString(),
      }).eq('id', adv.id);
      if (error) throw error;
      setAdvertisers(prev => prev.map(a => a.id === adv.id ? { ...a, account_status: 'approved', approved_at: new Date().toISOString() } : a));
      showSuccess(`${adv.company_name} approved!`);
    } catch (err: any) {
      setAdvertisersError(err.message || 'Failed to approve advertiser');
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleRejectAdvertiser = async (adv: Advertiser) => {
    if (!rejectionReason.trim()) { setAdvertisersError('Please enter a rejection reason.'); return; }
    setApprovalActionLoading(adv.id);
    try {
      const { error } = await supabase.from('advertisers').update({
        account_status: 'rejected',
        rejection_reason: rejectionReason.trim(),
        updated_at: new Date().toISOString(),
      }).eq('id', adv.id);
      if (error) throw error;
      setAdvertisers(prev => prev.map(a => a.id === adv.id ? { ...a, account_status: 'rejected', rejection_reason: rejectionReason.trim() } : a));
      setRejectingId(null);
      setRejectionReason('');
      showSuccess(`${adv.company_name} rejected.`);
    } catch (err: any) {
      setAdvertisersError(err.message || 'Failed to reject advertiser');
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleSuspendAdvertiser = async (adv: Advertiser) => {
    setApprovalActionLoading(adv.id);
    try {
      const newStatus = adv.account_status === 'suspended' ? 'approved' : 'suspended';
      const { error } = await supabase.from('advertisers').update({ account_status: newStatus, updated_at: new Date().toISOString() }).eq('id', adv.id);
      if (error) throw error;
      setAdvertisers(prev => prev.map(a => a.id === adv.id ? { ...a, account_status: newStatus } : a));
      showSuccess(newStatus === 'suspended' ? `${adv.company_name} suspended.` : `${adv.company_name} reactivated.`);
    } catch (err: any) {
      setAdvertisersError(err.message || 'Failed to update advertiser status');
    } finally {
      setApprovalActionLoading(null);
    }
  };

  const handleDeleteAdvertiser = async (advId: string) => {
    setAdvertiserDeleteLoading(true);
    try {
      const { error } = await supabase.from('advertisers').delete().eq('id', advId);
      if (error) throw error;
      setAdvertisers(prev => prev.filter(a => a.id !== advId));
      setAdvertiserDeleteConfirmId(null);
      showSuccess('Advertiser deleted successfully');
    } catch (err: any) {
      setAdvertisersError(err.message || 'Failed to delete advertiser');
    } finally {
      setAdvertiserDeleteLoading(false);
    }
  };

  // ── Revenue settlement handlers ──────────────────────────────────────────────
  const fetchSettlements = useCallback(async () => {
    setSettlementsLoading(true);
    setSettlementsError('');
    try {
      const { data, error } = await supabase
        .from('revenue_settlements')
        .select('*, advertisers(company_name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      setSettlements(data || []);
    } catch (err: any) {
      setSettlementsError(err.message || 'Failed to load settlements');
    } finally {
      setSettlementsLoading(false);
    }
  }, [supabase]);

  const handleSaveSettlement = async () => {
    if (!settlementForm.advertiser_id) { setSettlementFormError('Please select an advertiser.'); return; }
    if (!settlementForm.period_start || !settlementForm.period_end) { setSettlementFormError('Period start and end are required.'); return; }
    if (!settlementForm.amount_due) { setSettlementFormError('Amount due is required.'); return; }
    setSettlementFormLoading(true);
    setSettlementFormError('');
    try {
      const payload = {
        advertiser_id: settlementForm.advertiser_id,
        period_start: settlementForm.period_start,
        period_end: settlementForm.period_end,
        total_impressions: parseInt(settlementForm.total_impressions || '0'),
        total_clicks: parseInt(settlementForm.total_clicks || '0'),
        amount_due: parseFloat(settlementForm.amount_due || '0'),
        amount_paid: parseFloat(settlementForm.amount_paid || '0'),
        payment_status: settlementForm.payment_status,
        invoice_number: settlementForm.invoice_number.trim() || null,
        notes: settlementForm.notes.trim() || null,
        updated_at: new Date().toISOString(),
      };
      const { error } = await supabase.from('revenue_settlements').insert({ id: crypto.randomUUID(), ...payload });
      if (error) throw error;
      setSettlementForm({ advertiser_id: '', period_start: '', period_end: '', total_impressions: '', total_clicks: '', amount_due: '', amount_paid: '', payment_status: 'pending', invoice_number: '', notes: '' });
      setShowSettlementForm(false);
      showSuccess('Settlement report created!');
      fetchSettlements();
    } catch (err: any) {
      setSettlementFormError(err.message || 'Failed to create settlement');
    } finally {
      setSettlementFormLoading(false);
    }
  };

  const handleMarkSettlementPaid = async (s: RevenueSettlement) => {
    setSettlementMarkPaidId(s.id);
    try {
      const { error } = await supabase.from('revenue_settlements').update({
        payment_status: 'paid',
        amount_paid: s.amount_due,
        paid_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }).eq('id', s.id);
      if (error) throw error;
      setSettlements(prev => prev.map(r => r.id === s.id ? { ...r, payment_status: 'paid', amount_paid: s.amount_due } : r));
      showSuccess('Settlement marked as paid!');
    } catch (err: any) {
      setSettlementsError(err.message || 'Failed to mark as paid');
    } finally {
      setSettlementMarkPaidId(null);
    }
  };

  const handleDeleteSettlement = async (sId: string) => {
    setSettlementDeleteLoading(true);
    try {
      const { error } = await supabase.from('revenue_settlements').delete().eq('id', sId);
      if (error) throw error;
      setSettlements(prev => prev.filter(r => r.id !== sId));
      setSettlementDeleteConfirmId(null);
      showSuccess('Settlement deleted');
    } catch (err: any) {
      setSettlementsError(err.message || 'Failed to delete settlement');
    } finally {
      setSettlementDeleteLoading(false);
    }
  };

  const handleSetFeatured = async (video: DbVideo) => {
    setFeaturedSetLoadingId(video.id);
    setFeaturedError('');
    try {
      // Unset all featured first
      await supabase.from('videos').update({ is_featured: false }).eq('is_featured', true);
      // Set the selected video as featured
      const { error } = await supabase.from('videos').update({ is_featured: true }).eq('id', video.id);
      if (error) throw error;
      setFeaturedVideo(video);
      showSuccess(`"${video.title}" is now the featured video!`);
      // Refresh allVideos to reflect is_featured changes
      fetchAllVideos();
    } catch (err: any) {
      setFeaturedError(err.message || 'Failed to set featured video');
    } finally {
      setFeaturedSetLoadingId(null);
    }
  };

  const handleUnsetFeatured = async () => {
    if (!featuredVideo) return;
    setFeaturedSetLoadingId(featuredVideo.id);
    setFeaturedError('');
    try {
      const { error } = await supabase.from('videos').update({ is_featured: false }).eq('id', featuredVideo.id);
      if (error) throw error;
      setFeaturedVideo(null);
      showSuccess('Featured video removed. The site will auto-select from recent videos.');
      fetchAllVideos();
    } catch (err: any) {
      setFeaturedError(err.message || 'Failed to remove featured video');
    } finally {
      setFeaturedSetLoadingId(null);
    }
  };

  const handleAdminSearch = async () => {
    if (!adminSearchQuery.trim()) return;
    setAdminSearchLoading(true);
    setAdminSearchError('');
    try {
      const q = adminSearchQuery.trim().toLowerCase();
      const { data, error } = await supabase
        .from('user_profiles')
        .select('*')
        .or(`email.ilike.%${q}%,username.ilike.%${q}%,full_name.ilike.%${q}%`);
      if (error) throw error;
      setAdminSearchResults(data || []);
      if ((data || []).length === 0) setAdminSearchError('');
    } catch (err: any) {
      setAdminSearchError(err.message || 'Search failed');
    } finally {
      setAdminSearchLoading(false);
    }
  };

  const handleToggleAdmin = async (user: UserProfile, grant: boolean) => {
    setAdminToggleLoadingId(user.id);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({ is_admin: grant })
        .eq('id', user.id);
      if (error) throw error;
      setAdminSearchResults(prev => prev.map(u => u.id === user.id ? { ...u, is_admin: grant } : u));
      fetchAdmins();
      showSuccess(grant ? 'Admin access granted.' : 'Admin access revoked.');
    } catch (err: any) {
      setAdminsError(err.message || 'Failed to update admin status');
    } finally {
      setAdminToggleLoadingId(null);
    }
  };

  // Restore admin session from sessionStorage on mount
  useEffect(() => {
    try {
      if (sessionStorage.getItem('adminAuthenticated') === '1') {
        setAuthenticated(true);
      }
    } catch {}
  }, []);

  // When initialEditPostId is provided and admin is authenticated, open news tab with edit form
  useEffect(() => {
    if (authenticated && initialEditPostId) {
      setActiveTab('news');
      fetchNewsPosts().then(() => {}).catch(() => {});
    }
  }, [authenticated, initialEditPostId]);

  // After news posts load, open the edit form for the target post
  useEffect(() => {
    if (authenticated && initialEditPostId && newsPosts.length > 0) {
      const target = newsPosts.find(p => p.id === initialEditPostId);
      if (target) {
        openNewsForm(target);
      }
    }
  }, [authenticated, initialEditPostId, newsPosts]);

  useEffect(() => {
    if (authenticated && activeTab === 'users') {
      fetchUsers();
    }
    if (authenticated && activeTab === 'videos') {
      fetchAllVideos();
    }
    if (authenticated && activeTab === 'admins') {
      fetchAdmins();
      setAdminSearchQuery('');
      setAdminSearchResults([]);
    }
    if (authenticated && activeTab === 'featured') {
      fetchFeaturedVideo();
      fetchAllVideos();
    }
    if (authenticated && activeTab === 'ads') {
      fetchAds();
    }
    if (authenticated && activeTab === 'news') {
      fetchNewsPosts();
      fetchAds();
    }
    if (authenticated && (activeTab === 'advertisers' || activeTab === 'approvals')) {
      fetchAdvertisers();
    }
    if (authenticated && activeTab === 'revenue') {
      fetchSettlements();
      fetchAdvertisers();
      fetchAdPayments();
    }
    if (authenticated && activeTab === 'comments') {
      fetchAllVideos();
      fetchNewsPosts();
    }
    if (authenticated && activeTab === 'reports') {
      fetchReports();
    }
  }, [authenticated, activeTab, fetchUsers, fetchAllVideos, fetchAdmins, fetchFeaturedVideo, fetchAds, fetchNewsPosts, fetchAdvertisers, fetchSettlements, fetchAdPayments]);

  const handleAuth = async () => {
    if (!password.trim()) return;
    setAuthLoading(true);
    setAuthError('');
    try {
      const res = await fetch('/api/admin/verify', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await res.json();
      if (data.success) {
        setAuthenticated(true);
        try { sessionStorage.setItem('adminAuthenticated', '1'); } catch {}
      } else {
        setAuthError(data.error || 'Incorrect password. Try again.');
      }
    } catch {
      setAuthError('Unable to verify. Please try again.');
    } finally {
      setAuthLoading(false);
    }
  };

  const openEditUser = (user: UserProfile) => {
    setEditingUser(user);
    setEditForm({
      full_name: user.full_name || '',
      username: user.username || '',
      avatar_url: user.avatar_url || '',
    });
    setEditError('');
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setEditLoading(true);
    setEditError('');
    try {
      const { error } = await supabase
        .from('user_profiles')
        .update({
          full_name: editForm.full_name,
          username: editForm.username || null,
          avatar_url: editForm.avatar_url || '',
          updated_at: new Date().toISOString(),
        })
        .eq('id', editingUser.id);
      if (error) throw error;
      setUsers(prev => prev.map(u => u.id === editingUser.id ? { ...u, ...editForm } : u));
      setEditingUser(null);
      showSuccess('User profile updated successfully');
    } catch (err: any) {
      setEditError(err.message || 'Failed to update user');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteUser = async (userId: string) => {
    setDeleteLoading(true);
    try {
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);
      if (error) throw error;
      setUsers(prev => prev.filter(u => u.id !== userId));
      setDeleteConfirmId(null);
      showSuccess('User deleted successfully');
    } catch (err: any) {
      setUsersError(err.message || 'Failed to delete user');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleDeleteVideo = async (videoId: string) => {
    setVideoDeleteLoading(true);
    try {
      const { error } = await supabase
        .from('videos')
        .delete()
        .eq('id', videoId);
      if (error) throw error;
      setAllVideos(prev => prev.filter(v => v.id !== videoId));
      setVideoDeleteConfirmId(null);
      showSuccess('Video deleted successfully');
    } catch (err: any) {
      setVideosError(err.message || 'Failed to delete video');
    } finally {
      setVideoDeleteLoading(false);
    }
  };

  const handleSaveStats = async () => {
    if (!editStatsVideo) return;
    setEditStatsLoading(true);
    setEditStatsError('');
    try {
      const { data, error } = await supabase
        .from('videos')
        .update({ views: editStatsViews, likes_count: editStatsLikes })
        .eq('id', editStatsVideo.id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Update failed — permission denied or video not found. Check RLS policies.');
      }
      setAllVideos(prev => prev.map(v => v.id === editStatsVideo.id ? { ...v, views: editStatsViews, likes_count: editStatsLikes } : v));
      setEditStatsVideo(null);
      showSuccess('Stats updated successfully');
    } catch (err: any) {
      setEditStatsError(err.message || 'Failed to update stats');
    } finally {
      setEditStatsLoading(false);
    }
  };

  const handleSaveEditVideo = async () => {
    if (!editVideoTarget) return;
    setEditVideoLoading(true);
    setEditVideoError('');
    try {
      let thumbnailUrl = editVideoForm.thumbnail.trim();
      if (editThumbnailFile) {
        thumbnailUrl = await uploadThumbnailToStorage(editThumbnailFile);
      }
      const updates: Record<string, any> = {
        title: editVideoForm.title.trim(),
        description: editVideoForm.description.trim(),
        tag: editVideoForm.tag.trim(),
        category: editVideoForm.category.trim(),
        video_status: editVideoForm.video_status,
        thumbnail: thumbnailUrl,
        thumbnail_alt: editVideoForm.thumbnail_alt.trim(),
      };
      const { data, error } = await supabase
        .from('videos')
        .update(updates)
        .eq('id', editVideoTarget.id)
        .select('id');
      if (error) throw error;
      if (!data || data.length === 0) {
        throw new Error('Update failed — permission denied or video not found. Check RLS policies.');
      }
      setAllVideos(prev => prev.map(v => v.id === editVideoTarget.id ? { ...v, ...updates } : v));
      setEditVideoTarget(null);
      setEditThumbnailFile(null);
      setEditThumbnailPreview(null);
      showSuccess('Video updated successfully');
    } catch (err: any) {
      setEditVideoError(err.message || 'Failed to update video');
    } finally {
      setEditVideoLoading(false);
    }
  };

  const handleSaveNewsViews = async () => {
    if (!editNewsViewsPost) return;
    setEditNewsViewsLoading(true);
    setEditNewsViewsError('');
    try {
      const { error } = await supabase
        .from('news_posts')
        .update({ views: editNewsViewsCount })
        .eq('id', editNewsViewsPost.id);
      if (error) throw error;
      setNewsPosts(prev => prev.map(p => p.id === editNewsViewsPost.id ? { ...p, views: editNewsViewsCount } : p));
      setEditNewsViewsPost(null);
      showSuccess('Blog post views updated successfully');
    } catch (err: any) {
      setEditNewsViewsError(err.message || 'Failed to update views');
    } finally {
      setEditNewsViewsLoading(false);
    }
  };

  const showSuccess = (msg: string) => {
    setSuccessMsg(msg);
    setTimeout(() => setSuccessMsg(''), 3000);
  };

  const getEmbedUrl = (video: Video): string | null => {
    if (video.source === 'youtube' && video.embedId) {
      return `https://www.youtube.com/embed/${video.embedId}?autoplay=1&rel=0`;
    }
    if (video.source === 'vimeo' && video.embedId) {
      return `https://player.vimeo.com/video/${video.embedId}?autoplay=1`;
    }
    if (video.source === 'upload' && video.url) {
      return video.url;
    }
    return null;
  };

  const extractYouTubeId = (url: string): string | null => {
    // Handle plain video IDs
    if (/^[a-zA-Z0-9_-]{11}$/.test(url.trim())) return url.trim();
    try {
      const parsed = new URL(url);
      // youtu.be short links
      if (parsed.hostname === 'youtu.be') {
        const id = parsed.pathname.replace(/^\//, '').split('/')[0];
        if (/^[a-zA-Z0-9_-]{11}$/.test(id)) return id;
      }
      // youtube.com variants (www, m, music, etc.)
      if (parsed.hostname.endsWith('youtube.com')) {
        // /shorts/, /embed/, /v/ paths
        const pathMatch = parsed.pathname.match(/\/(?:shorts|embed|v)\/([a-zA-Z0-9_-]{11})/);
        if (pathMatch) return pathMatch[1];
        // /watch?v= query param
        const v = parsed.searchParams.get('v');
        if (v && /^[a-zA-Z0-9_-]{11}$/.test(v)) return v;
      }
    } catch {
      // URL parsing failed, fall back to regex
    }
    // Fallback regex for malformed URLs
    const patterns = [
      /[?&]v=([a-zA-Z0-9_-]{11})/,
      /youtu\.be\/([a-zA-Z0-9_-]{11})/,
      /\/(?:shorts|embed|v)\/([a-zA-Z0-9_-]{11})/,
    ];
    for (const pattern of patterns) {
      const match = url.match(pattern);
      if (match) return match[1];
    }
    return null;
  };

  const extractVimeoId = (url: string): string | null => {
    const match = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    return match ? match[1] : null;
  };

  const getAdminYouTubeThumbnailOptions = (videoId: string): { url: string; label: string }[] => [
    { url: `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`, label: 'Max Quality' },
    { url: `https://img.youtube.com/vi/${videoId}/sddefault.jpg`, label: 'Standard' },
    { url: `https://img.youtube.com/vi/${videoId}/hqdefault.jpg`, label: 'High Quality' },
    { url: `https://img.youtube.com/vi/${videoId}/mqdefault.jpg`, label: 'Mid Quality' },
  ];

  const getAdminVimeoThumbnailOptions = (videoId: string): { url: string; label: string }[] => [
    { url: `https://vumbnail.com/${videoId}.jpg`, label: 'Default' },
    { url: `https://vumbnail.com/${videoId}_large.jpg`, label: 'Large' },
    { url: `https://vumbnail.com/${videoId}_medium.jpg`, label: 'Medium' },
    { url: `https://vumbnail.com/${videoId}_small.jpg`, label: 'Small' },
  ];

  const handleLoadAdminThumbnails = () => {
    if (!videoUrl.trim()) { setUploadError('Please enter a video URL first.'); return; }
    if (!uploadForm.title.trim()) { setUploadError('Title is required.'); return; }
    if (!uploadForm.tag.trim()) { setUploadError('Tag is required.'); return; }
    setUploadError('');

    if (uploadSourceType === 'youtube') {
      const id = extractYouTubeId(videoUrl.trim());
      if (!id) { setUploadError('Invalid YouTube URL.'); return; }
      const opts = getAdminYouTubeThumbnailOptions(id);
      setAdminThumbnailOptions(opts);
      setAdminSelectedThumbnail(opts[2].url); // default to hqdefault
    } else if (uploadSourceType === 'vimeo') {
      const id = extractVimeoId(videoUrl.trim());
      if (!id) { setUploadError('Invalid Vimeo URL.'); return; }
      const opts = getAdminVimeoThumbnailOptions(id);
      setAdminThumbnailOptions(opts);
      setAdminSelectedThumbnail(opts[0].url);
    }
    setAdminThumbnailLoadErrors({});
    setAdminShowThumbnailPicker(true);
  };

  const handleSubmitUrlVideo = async () => {
    if (!videoUrl.trim()) { setUploadError('Please enter a video URL.'); return; }
    if (!uploadForm.title.trim()) { setUploadError('Title is required.'); return; }
    if (!uploadForm.tag.trim()) { setUploadError('Tag is required.'); return; }

    let embedId: string | null = null;
    const source: string = uploadSourceType;

    if (uploadSourceType === 'youtube') {
      embedId = extractYouTubeId(videoUrl.trim());
      if (!embedId) { setUploadError('Invalid YouTube URL. Please use a valid youtube.com or youtu.be link.'); return; }
    } else if (uploadSourceType === 'vimeo') {
      embedId = extractVimeoId(videoUrl.trim());
      if (!embedId) { setUploadError('Invalid Vimeo URL. Please use a valid vimeo.com link.'); return; }
    }

    setUploadLoading(true);
    setUploadError('');

    try {
      // Upload custom thumbnail if provided, else use selected thumbnail from picker
      let thumbnailUrl = uploadForm.thumbnail.trim();
      if (customThumbnailFile) {
        thumbnailUrl = await uploadThumbnailToStorage(customThumbnailFile);
      } else if (adminSelectedThumbnail) {
        thumbnailUrl = adminSelectedThumbnail;
      } else if (!thumbnailUrl) {
        thumbnailUrl = uploadSourceType === 'youtube' && embedId
          ? `https://img.youtube.com/vi/${embedId}/hqdefault.jpg`
          : 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7';
      }

      const newVideo = {
        id: crypto.randomUUID(),
        title: uploadForm.title.trim(),
        tag: uploadForm.tag.trim().toUpperCase(),
        description: uploadForm.description.trim(),
        source,
        url: videoUrl.trim(),
        embed_id: embedId,
        thumbnail: thumbnailUrl,
        thumbnail_alt: uploadForm.thumbnailAlt.trim() || uploadForm.title.trim(),
        views: 0,
        comments_count: 0,
        submitted_by: uploadForm.submittedBy.trim() || 'Admin',
        submitted_at: new Date().toISOString().split('T')[0],
        video_status: 'approved',
        category: uploadForm.category,
      };

      const { error } = await supabase.from('videos').insert(newVideo);
      if (error) throw error;

      setUploadForm({ title: '', tag: '', description: '', category: VIDEO_CATEGORIES[0], submittedBy: '', thumbnail: '', thumbnailAlt: '' });
      setVideoUrl('');
      setCustomThumbnailFile(null);
      setCustomThumbnailPreview(null);
      setAdminThumbnailOptions([]);
      setAdminSelectedThumbnail('');
      setAdminShowThumbnailPicker(false);
      setAdminThumbnailLoadErrors({});
      showSuccess(`${uploadSourceType === 'youtube' ? 'YouTube' : 'Vimeo'} video added and published successfully!`);
      setActiveTab('videos');
      fetchAllVideos();
    } catch (err: any) {
      setUploadError(err.message || 'Failed to add video. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setUploadFile(file);
    if (file) {
      const url = URL.createObjectURL(file);
      setUploadPreviewUrl(url);
    } else {
      setUploadPreviewUrl(null);
    }
  };

  const handleUploadVideo = async () => {
    if (!uploadFile) { setUploadError('Please select a video file.'); return; }
    if (!uploadForm.title.trim()) { setUploadError('Title is required.'); return; }
    if (!uploadForm.tag.trim()) { setUploadError('Tag is required.'); return; }

    setUploadLoading(true);
    setUploadError('');
    setUploadProgress(0);

    try {
      const publicUrl = await uploadVideoToStorage(uploadFile, (p) => setUploadProgress(p));

      const newVideo = {
        id: crypto.randomUUID(),
        title: uploadForm.title.trim(),
        tag: uploadForm.tag.trim().toUpperCase(),
        description: uploadForm.description.trim(),
        source: 'upload',
        url: publicUrl,
        embed_id: null,
        thumbnail: uploadForm.thumbnail.trim() || 'https://images.unsplash.com/photo-1611162617213-7d7a39e9b1d7',
        thumbnail_alt: uploadForm.thumbnailAlt.trim() || uploadForm.title.trim(),
        views: 0,
        comments_count: 0,
        submitted_by: uploadForm.submittedBy.trim() || 'Admin',
        submitted_at: new Date().toISOString().split('T')[0],
        video_status: 'approved',
        category: uploadForm.category,
      };

      const { error } = await supabase.from('videos').insert(newVideo);
      if (error) throw error;

      setUploadForm({ title: '', tag: '', description: '', category: VIDEO_CATEGORIES[0], submittedBy: '', thumbnail: '', thumbnailAlt: '' });
      setUploadFile(null);
      setUploadPreviewUrl(null);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
      showSuccess('Video uploaded and published successfully!');
      setActiveTab('videos');
      fetchAllVideos();
    } catch (err: any) {
      setUploadError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploadLoading(false);
    }
  };

  const filteredVideos = allVideos.filter(v =>
    !videoSearchQuery ||
    v.title?.toLowerCase().includes(videoSearchQuery.toLowerCase()) ||
    v.submitted_by?.toLowerCase().includes(videoSearchQuery.toLowerCase()) ||
    v.tag?.toLowerCase().includes(videoSearchQuery.toLowerCase())
  );

  // ─── Auth Gate ───────────────────────────────────────────────────────────────
  if (!authenticated) {
    return (
      <div className="modal-overlay animate-fade-in" onClick={(e) => e.target === e.currentTarget && onClose()}>
        <div className="modal-content animate-slide-up max-w-sm">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stroke-primary">
            <div className="flex items-center gap-3">
              <div className="w-1 h-6 bg-brand-red"></div>
              <h2 className="text-xl font-bold text-text-primary uppercase tracking-wide">Admin Access</h2>
            </div>
            <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="px-6 py-8">
            <div className="flex justify-center mb-6">
              <div className="w-16 h-16 bg-surface-tertiary border border-stroke-primary rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-brand-red" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </div>
            </div>
            <p className="text-text-secondary text-sm text-center mb-5">Enter admin password to access the moderation panel</p>
            <div className="relative mb-3">
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleAuth(); }}
                placeholder="Admin password"
                className="input-field w-full pr-10"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary hover:text-text-primary transition-colors"
                tabIndex={-1}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                  </svg>
                )}
              </button>
            </div>
            {authError && <p className="text-brand-red text-sm mb-3">{authError}</p>}
            <button onClick={handleAuth} disabled={authLoading} className="btn-primary w-full justify-center disabled:opacity-60">
              {authLoading ? 'Verifying…' : 'Access Admin Panel'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ─── Video Preview Modal ──────────────────────────────────────────────────────
  if (previewVideo) {
    const embedUrl = getEmbedUrl(previewVideo);
    return (
      <div className="modal-overlay animate-fade-in" onClick={(e) => e.target === e.currentTarget && setPreviewVideo(null)}>
        <div className="modal-content animate-slide-up max-w-3xl w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stroke-primary">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setPreviewVideo(null)}
                className="text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1 text-sm"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div className="w-px h-5 bg-stroke-primary"></div>
              <div className="w-1 h-6 bg-brand-red"></div>
              <h2 className="text-base font-bold text-text-primary uppercase tracking-wide line-clamp-1">Preview: {previewVideo.title}</h2>
            </div>
            <button onClick={() => setPreviewVideo(null)} className="text-text-secondary hover:text-text-primary transition-colors p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="overflow-y-auto" style={{ maxHeight: '75vh' }}>
            <div className="relative bg-black" style={{ aspectRatio: '16/9' }}>
              {embedUrl ? (
                previewVideo.source === 'upload' ? (
                  <video src={embedUrl} controls autoPlay className="w-full h-full" />
                ) : (
                  <iframe
                    src={embedUrl}
                    className="w-full h-full"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    title={previewVideo.title}
                  />
                )
              ) : (
                <div className="w-full h-full flex flex-col items-center justify-center gap-3">
                  <img src={previewVideo.thumbnail} alt={previewVideo.thumbnailAlt} className="absolute inset-0 w-full h-full object-cover opacity-30" />
                  <div className="relative z-10 text-center">
                    <p className="text-text-muted text-sm">No embed available</p>
                  </div>
                </div>
              )}
            </div>
            <div className="p-5">
              <div className="flex items-start justify-between gap-4 mb-4">
                <div>
                  {previewVideo.tag && <span className="tag-label mr-1 text-xs">{previewVideo.tag}</span>}
                  <h3 className="text-text-primary text-lg font-normal leading-snug">{previewVideo.title}</h3>
                  {previewVideo.description && <p className="text-text-secondary text-sm mt-2">{previewVideo.description}</p>}
                </div>
                <span className="pending-badge flex-shrink-0">Pending</span>
              </div>
              <div className="flex gap-3">
                <button
                  onClick={() => { onApprove(previewVideo.id); setPreviewVideo(null); }}
                  className="flex-1 flex items-center justify-center gap-2 bg-green-700 hover:bg-green-600 text-white font-bold px-4 py-2.5 rounded-[4px] transition-colors duration-150"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  Approve Video
                </button>
                <button
                  onClick={() => { onReject(previewVideo.id); setPreviewVideo(null); }}
                  className="flex-1 flex items-center justify-center gap-2 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white font-bold px-4 py-2.5 rounded-[4px] transition-colors duration-150"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Reject Video
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Edit User Modal ──────────────────────────────────────────────────────────
  if (editingUser) {
    return (
      <div className="modal-overlay animate-fade-in" onClick={(e) => e.target === e.currentTarget && setEditingUser(null)}>
        <div className="modal-content animate-slide-up max-w-md w-full">
          <div className="flex items-center justify-between px-6 py-4 border-b border-stroke-primary">
            <div className="flex items-center gap-3">
              <button onClick={() => setEditingUser(null)} className="text-text-secondary hover:text-text-primary transition-colors flex items-center gap-1 text-sm">
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Back
              </button>
              <div className="w-px h-5 bg-stroke-primary"></div>
              <div className="w-1 h-6 bg-brand-red"></div>
              <h2 className="text-base font-bold text-text-primary uppercase tracking-wide">Edit User</h2>
            </div>
            <button onClick={() => setEditingUser(null)} className="text-text-secondary hover:text-text-primary transition-colors p-1">
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="p-6">
            <div className="flex items-center gap-4 mb-6 p-4 bg-surface-tertiary border border-stroke-primary rounded-[4px]">
              <div className="w-12 h-12 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0 overflow-hidden">
                {editForm.avatar_url ? (
                  <img src={editForm.avatar_url} alt="Avatar" className="w-full h-full object-cover" />
                ) : (
                  <span className="text-white text-lg font-bold uppercase">
                    {(editForm.full_name || editingUser.email || 'U').charAt(0)}
                  </span>
                )}
              </div>
              <div>
                <p className="text-text-primary text-sm font-bold">{editingUser.email}</p>
                <p className="text-text-muted text-xs">ID: {editingUser.id.slice(0, 8)}…</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Full Name</label>
                <input
                  type="text"
                  value={editForm.full_name}
                  onChange={(e) => setEditForm(prev => ({ ...prev, full_name: e.target.value }))}
                  placeholder="Full name"
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="Username (optional)"
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Avatar URL</label>
                <input
                  type="text"
                  value={editForm.avatar_url}
                  onChange={(e) => setEditForm(prev => ({ ...prev, avatar_url: e.target.value }))}
                  placeholder="https://... (optional)"
                  className="input-field"
                />
              </div>
            </div>
            {editError && <p className="text-brand-red text-sm mt-3">{editError}</p>}
            <div className="flex gap-3 mt-6">
              <button onClick={handleSaveUser} disabled={editLoading} className="flex-1 btn-primary justify-center disabled:opacity-60">
                {editLoading ? 'Saving…' : 'Save Changes'}
              </button>
              <button
                onClick={() => setEditingUser(null)}
                className="flex-1 flex items-center justify-center border border-stroke-primary text-text-secondary hover:text-text-primary hover:border-stroke-light font-bold px-4 py-2 rounded-[4px] transition-colors text-sm uppercase tracking-wide"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ─── Main Admin Panel ─────────────────────────────────────────────────────────
  return (
    <div className="modal-overlay animate-fade-in" onClick={(e) => e.target === e.currentTarget && onClose()}>
      <div className="modal-content animate-slide-up w-full" style={{ maxHeight: '95vh', maxWidth: '960px', width: '100%' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-stroke-primary flex-shrink-0">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-1 h-6 bg-brand-red"></div>
            <h2 className="text-lg sm:text-xl font-bold text-text-primary uppercase tracking-wide">Admin Panel</h2>
            {pendingVideos.length > 0 && (
              <span className="bg-brand-red text-white text-xs font-bold px-2 py-0.5 rounded-full">
                {pendingVideos.length}
              </span>
            )}
          </div>
          <button onClick={onClose} className="text-text-secondary hover:text-text-primary transition-colors p-1">
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Success message */}
        {successMsg && (
          <div className="mx-4 sm:mx-6 mt-3 px-4 py-2 bg-green-900 border border-green-700 text-green-300 text-sm rounded-[4px] flex items-center gap-2">
            <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
            </svg>
            {successMsg}
          </div>
        )}

        {/* Tabs */}
        <div className="px-2 sm:px-6 pt-2 sm:pt-3 border-b border-stroke-primary flex-shrink-0">
          {/* Mobile: dropdown selector */}
          <div className="block sm:hidden pb-2">
            <select
              value={activeTab}
              onChange={e => setActiveTab(e.target.value as AdminTab)}
              className="w-full bg-bg-secondary text-text-primary border border-stroke-primary rounded-lg px-3 py-2 text-sm font-semibold focus:outline-none focus:border-brand-red"
            >
              {([
                { id: 'pending', label: 'Pending', badge: pendingVideos.length },
                { id: 'videos', label: 'Videos' },
                { id: 'upload', label: 'Upload' },
                { id: 'users', label: 'Users' },
                { id: 'admins', label: 'Admins' },
                { id: 'featured', label: '⭐ Featured' },
                { id: 'ads', label: '📢 Ads' },
                { id: 'news', label: 'News' },
                { id: 'advertisers', label: '🏢 Advertisers' },
                { id: 'approvals', label: '✅ Approvals' },
                { id: 'revenue', label: '💰 Revenue' },
                { id: 'comments', label: '💬 Comments' },
                { id: 'reports', label: '🚩 Reports', badge: reports.filter(r => r.status === 'pending').length },
                { id: 'guide', label: 'Guide' },
              ] as { id: AdminTab; label: string; badge?: number }[]).map(tab => (
                <option key={tab.id} value={tab.id}>
                  {tab.label}{tab.badge !== undefined && tab.badge > 0 ? ` (${tab.badge})` : ''}
                </option>
              ))}
              <option value="__video_ads__" disabled>🎬 Video Ads ↗</option>
            </select>
          </div>

          {/* Desktop: horizontal tab buttons */}
          <div className="hidden sm:flex flex-wrap gap-0.5">
          {([
            { id: 'pending', label: 'Pending', badge: pendingVideos.length },
            { id: 'videos', label: 'Videos' },
            { id: 'upload', label: 'Upload' },
            { id: 'users', label: 'Users' },
            { id: 'admins', label: 'Admins' },
            { id: 'featured', label: '⭐ Featured' },
            { id: 'ads', label: '📢 Ads' },
            { id: 'news', label: 'News' },
            { id: 'advertisers', label: '🏢 Advertisers' },
            { id: 'approvals', label: '✅ Approvals' },
            { id: 'revenue', label: '💰 Revenue' },
            { id: 'comments', label: '💬 Comments' },
            { id: 'reports', label: '🚩 Reports', badge: reports.filter(r => r.status === 'pending').length },
            { id: 'guide', label: 'Guide' },
          ] as { id: AdminTab; label: string; badge?: number }[]).map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors duration-150 whitespace-nowrap mb-0.5 ${
                activeTab === tab.id
                  ? 'border-brand-red text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'
              }`}
            >
              {tab.label}
              {tab.badge !== undefined && tab.badge > 0 && (
                <span className="bg-brand-red text-white text-xs font-bold px-1.5 py-0.5 rounded-full leading-none">{tab.badge}</span>
              )}
            </button>
          ))}
          <Link
            href="/admin/video-ads"
            className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-bold uppercase tracking-wide border-b-2 border-transparent text-text-secondary hover:text-text-primary transition-colors duration-150 whitespace-nowrap mb-0.5"
          >
            🎬 Video Ads
          </Link>
          </div>
        </div>

        {/* Tab Content */}
        <div className="overflow-y-auto flex-1 p-3 sm:p-6" style={{ maxHeight: 'calc(95vh - 160px)' }}>

          {/* ── NEWS TAB ── */}
          {activeTab === 'news' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">News & Blog Posts</h3>
                  <p className="text-text-muted text-xs mt-0.5">Create and manage news articles shown on the News page</p>
                </div>
                <button
                  onClick={() => openNewsForm()}
                  className="btn-primary text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  New Post
                </button>
              </div>

              {/* News Post Form */}
              {showNewsForm && (
                <div className="mb-6 border border-stroke-primary bg-surface-secondary p-5 rounded-[4px]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-text-primary font-bold uppercase text-sm tracking-wide">
                      {editingPost ? 'Edit Post' : 'Create New Post'}
                    </h4>
                    <button onClick={() => { setShowNewsForm(false); setEditingPost(null); }} className="text-text-secondary hover:text-text-primary transition-colors">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Title *</label>
                      <input
                        type="text"
                        value={newsForm.title}
                        onChange={(e) => {
                          const title = e.target.value;
                          setNewsForm(prev => ({ ...prev, title, slug: prev.slug || generateSlug(title) }));
                        }}
                        placeholder="Post title..."
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Slug (URL)</label>
                      <input
                        type="text"
                        value={newsForm.slug}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, slug: e.target.value }))}
                        placeholder="auto-generated-from-title"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Categories <span className="normal-case font-normal text-text-muted">(select one or more)</span></label>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5 p-3 border border-stroke-primary rounded-[4px] bg-surface-overlay">
                        {['News','Sports','Entertainment','Music','Culture','Lifestyle','Technology','Business','Politics','Opinion','Interview','Events'].map(cat => (
                          <label key={cat} className="flex items-center gap-2 cursor-pointer group">
                            <input
                              type="checkbox"
                              checked={newsFormCategories.includes(cat)}
                              onChange={(e) => {
                                if (e.target.checked) {
                                  setNewsFormCategories(prev => [...prev, cat]);
                                } else {
                                  setNewsFormCategories(prev => prev.filter(c => c !== cat));
                                }
                              }}
                              className="w-3.5 h-3.5 accent-brand-red flex-shrink-0"
                            />
                            <span className="text-text-secondary text-xs group-hover:text-text-primary transition-colors">{cat}</span>
                          </label>
                        ))}
                      </div>
                      {newsFormCategories.length === 0 && (
                        <p className="text-yellow-400 text-xs mt-1">⚠ Select at least one category</p>
                      )}
                      {newsFormCategories.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {newsFormCategories.map(cat => (
                            <span key={cat} className="text-xs px-2 py-0.5 bg-brand-red/20 text-brand-red border border-brand-red/30 rounded-full">{cat}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Tags <span className="normal-case font-normal text-text-muted">(press Enter or comma to add)</span></label>
                      <div className="flex flex-wrap gap-1.5 p-2 border border-stroke-primary rounded-[4px] bg-surface-overlay min-h-[40px] cursor-text" onClick={() => document.getElementById('news-tag-input')?.focus()}>
                        {newsFormTags.map(tag => (
                          <span key={tag} className="flex items-center gap-1 text-xs px-2 py-0.5 bg-surface-tertiary border border-stroke-primary text-text-secondary rounded-full">
                            {tag}
                            <button
                              type="button"
                              onClick={(e) => { e.stopPropagation(); setNewsFormTags(prev => prev.filter(t => t !== tag)); }}
                              className="text-text-muted hover:text-brand-red transition-colors leading-none"
                              aria-label={`Remove tag ${tag}`}
                            >×</button>
                          </span>
                        ))}
                        <input
                          id="news-tag-input"
                          type="text"
                          value={newsFormTagInput}
                          onChange={(e) => setNewsFormTagInput(e.target.value)}
                          onKeyDown={(e) => {
                            if ((e.key === 'Enter' || e.key === ',') && newsFormTagInput.trim()) {
                              e.preventDefault();
                              const newTag = newsFormTagInput.trim().replace(/,$/, '').toLowerCase();
                              if (newTag && !newsFormTags.includes(newTag)) {
                                setNewsFormTags(prev => [...prev, newTag]);
                              }
                              setNewsFormTagInput('');
                            } else if (e.key === 'Backspace' && !newsFormTagInput && newsFormTags.length > 0) {
                              setNewsFormTags(prev => prev.slice(0, -1));
                            }
                          }}
                          onBlur={() => {
                            if (newsFormTagInput.trim()) {
                              const newTag = newsFormTagInput.trim().replace(/,$/, '').toLowerCase();
                              if (newTag && !newsFormTags.includes(newTag)) {
                                setNewsFormTags(prev => [...prev, newTag]);
                              }
                              setNewsFormTagInput('');
                            }
                          }}
                          placeholder={newsFormTags.length === 0 ? 'e.g. breaking-news, sports, local...' : ''}
                          className="flex-1 min-w-[120px] bg-transparent text-text-primary text-xs outline-none placeholder:text-text-muted"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Author</label>
                      <input
                        type="text"
                        value={newsForm.author}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, author: e.target.value }))}
                        placeholder="ViddStar"
                        className="input-field"
                      />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Cover Image URL</label>
                      <input
                        type="text"
                        value={newsForm.cover_image}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, cover_image: e.target.value }))}
                        placeholder="https://..."
                        className="input-field"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Or Upload Cover Image</label>
                      <input
                        ref={newsCoverInputRef}
                        type="file"
                        accept="image/*"
                        onChange={handleNewsCoverChange}
                        className="input-field text-sm"
                      />
                      {newsCoverPreview && (
                        <img src={newsCoverPreview} alt="Cover preview" className="mt-2 h-24 object-cover rounded-[4px] border border-stroke-primary" />
                      )}
                    </div>

                    {/* Gallery Images */}
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                        📷 Photo Gallery
                        <span className="text-text-muted font-normal normal-case ml-1">(upload multiple photos — shown in article gallery)</span>
                      </label>
                      <div
                        className="border-2 border-dashed border-stroke-primary hover:border-brand-red transition-colors rounded-[4px] p-4 text-center cursor-pointer"
                        onClick={() => newsGalleryInputRef.current?.click()}
                      >
                        <svg className="w-8 h-8 text-text-muted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.553-4.553a2 2 0 012.827 0L11 16m-2-2l1.586-1.586a2 2 0 012.827 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-text-secondary text-sm font-medium">Click to upload photos</p>
                        <p className="text-text-muted text-xs mt-1">JPG, PNG, WebP — multiple files supported</p>
                      </div>
                      <input
                        ref={newsGalleryInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={(e) => {
                          const files = Array.from(e.target.files || []);
                          setNewsGalleryFiles(prev => [...prev, ...files]);
                          const previews = files.map(f => URL.createObjectURL(f));
                          setNewsGalleryPreviews(prev => [...prev, ...previews]);
                        }}
                      />
                      {/* Existing gallery images (when editing) */}
                      {newsGalleryExisting.length > 0 && (
                        <div className="mt-3">
                          <p className="text-text-muted text-xs mb-2">Existing photos ({newsGalleryExisting.length}) — click × to remove</p>
                          <div className="flex flex-wrap gap-2">
                            {newsGalleryExisting.map((url, idx) => (
                              <div key={idx} className="relative group">
                                <img src={url} alt={`Gallery ${idx + 1}`} className="h-20 w-28 object-cover rounded-[4px] border border-stroke-primary" />
                                <button
                                  type="button"
                                  onClick={() => setNewsGalleryExisting(prev => prev.filter((_, i) => i !== idx))}
                                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-brand-red"
                                  aria-label="Remove photo"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      {/* New gallery previews */}
                      {newsGalleryPreviews.length > 0 && (
                        <div className="mt-3">
                          <p className="text-text-muted text-xs mb-2">New photos to upload ({newsGalleryPreviews.length})</p>
                          <div className="flex flex-wrap gap-2">
                            {newsGalleryPreviews.map((url, idx) => (
                              <div key={idx} className="relative group">
                                <img src={url} alt={`New gallery ${idx + 1}`} className="h-20 w-28 object-cover rounded-[4px] border border-stroke-primary" />
                                <button
                                  type="button"
                                  onClick={() => {
                                    setNewsGalleryFiles(prev => prev.filter((_, i) => i !== idx));
                                    setNewsGalleryPreviews(prev => prev.filter((_, i) => i !== idx));
                                  }}
                                  className="absolute top-1 right-1 bg-black/70 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs opacity-0 group-hover:opacity-100 transition-opacity hover:bg-brand-red"
                                  aria-label="Remove photo"
                                >
                                  ×
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Ad Space */}
                    <div className="md:col-span-2 border border-stroke-primary rounded-[4px] p-4 bg-surface-tertiary">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-1 h-4 bg-brand-red inline-block rounded-sm flex-shrink-0" />
                        <label className="text-text-primary text-xs font-bold uppercase tracking-wide">Ad Space</label>
                        <span className="text-text-muted text-xs font-normal normal-case ml-1">— place an ad inside this post</span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Ad Position</label>
                          <select
                            value={newsForm.ad_placement}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, ad_placement: e.target.value }))}
                            className="input-field"
                          >
                            <option value="bottom">Bottom of article (default)</option>
                            <option value="top">Top of article (above content)</option>
                            <option value="middle">Middle of article (between paragraphs)</option>
                            <option value="none">No ad</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Select Ad</label>
                          <select
                            value={newsForm.ad_id}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, ad_id: e.target.value }))}
                            className="input-field"
                            disabled={newsForm.ad_placement === 'none'}
                          >
                            <option value="">— Auto (any active blog-post ad) —</option>
                            {ads.filter(a => a.is_active).map(a => (
                              <option key={a.id} value={a.id}>{a.title} ({a.advertiser || 'no advertiser'})</option>
                            ))}
                          </select>
                          <p className="text-text-muted text-xs mt-1">Leave blank to auto-rotate active ads</p>
                        </div>
                      </div>
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Excerpt / Summary</label>
                      <textarea
                        value={newsForm.excerpt}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, excerpt: e.target.value }))}
                        placeholder="Short summary shown in the news listing..."
                        rows={2}
                        className="input-field resize-none"
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Full Content</label>
                      {/* Content toolbar */}
                      <div className="flex items-center gap-2 mb-1.5 p-2 bg-surface-tertiary border border-stroke-primary rounded-t-[4px] border-b-0">
                        <button
                          type="button"
                          onClick={() => newsInlineImageInputRef.current?.click()}
                          disabled={newsInlineImageUploading}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 bg-surface-overlay hover:bg-brand-red/20 border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-brand-red rounded-[4px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                          title="Upload an image and insert it into the content"
                        >
                          {newsInlineImageUploading ? (
                            <div className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.553-4.553a2 2 0 012.827 0L11 16m-2-2l1.586-1.586a2 2 0 012.827 0L19 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                          )}
                          {newsInlineImageUploading ? 'Uploading…' : 'Insert Image'}
                        </button>
                        <button
                          type="button"
                          onClick={handleInsertLink}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 bg-surface-overlay hover:bg-brand-red/20 border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-brand-red rounded-[4px] transition-colors"
                          title="Highlight text then click to wrap it with a link"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                          Insert Link
                        </button>
                        <button
                          type="button"
                          onClick={handleInsertYouTube}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 bg-surface-overlay hover:bg-red-600/20 border border-stroke-primary hover:border-red-500 text-text-secondary hover:text-red-500 rounded-[4px] transition-colors"
                          title="Paste a YouTube URL to embed the video in the post"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M23.498 6.186a3.016 3.016 0 00-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 00.502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 002.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 002.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                          </svg>
                          Insert YouTube
                        </button>
                        <button
                          type="button"
                          onClick={handleInsertTwitter}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 bg-surface-overlay hover:bg-sky-500/20 border border-stroke-primary hover:border-sky-500 text-text-secondary hover:text-sky-400 rounded-[4px] transition-colors"
                          title="Paste a Twitter/X post URL to embed it in the post"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.253 5.622zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                          </svg>
                          Insert X/Twitter
                        </button>
                        <button
                          type="button"
                          onClick={handleInsertInstagram}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase px-2.5 py-1.5 bg-surface-overlay hover:bg-pink-500/20 border border-stroke-primary hover:border-pink-500 text-text-secondary hover:text-pink-400 rounded-[4px] transition-colors"
                          title="Paste an Instagram post URL to embed it in the post"
                        >
                          <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                          </svg>
                          Insert Instagram
                        </button>
                        <span className="text-text-muted text-xs">Supports Markdown — use **bold**, *italic*, ## Heading, [link](url)</span>
                        <input
                          ref={newsInlineImageInputRef}
                          type="file"
                          accept="image/jpeg,image/png,image/gif,image/webp"
                          className="hidden"
                          onChange={handleNewsInlineImageUpload}
                        />
                      </div>
                      <textarea
                        ref={newsContentRef}
                        value={newsForm.content}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, content: e.target.value }))}
                        placeholder="Full article content... (Markdown supported)"
                        rows={10}
                        className="input-field resize-y rounded-t-none"
                      />
                    </div>
                    <div className="flex items-center gap-6">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newsForm.is_published}
                          onChange={(e) => setNewsForm(prev => ({ ...prev, is_published: e.target.checked }))}
                          className="w-4 h-4 accent-brand-red"
                          disabled={!!newsForm.scheduled_at}
                        />
                        <span className={`text-text-secondary text-sm ${newsForm.scheduled_at ? 'opacity-50' : ''}`}>Published</span>
                      </label>
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newsForm.is_featured}
                          onChange={(e) => setNewsForm(prev => ({ ...prev, is_featured: e.target.checked }))}
                          className="w-4 h-4 accent-brand-red"
                        />
                        <span className="text-text-secondary text-sm">Featured (shown prominently)</span>
                      </label>
                    </div>
                    <div className="mt-3">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Schedule Publish Date &amp; Time <span className="normal-case text-text-muted font-normal">(optional — leave blank to publish immediately)</span></label>
                      <input
                        type="datetime-local"
                        value={newsForm.scheduled_at}
                        onChange={(e) => setNewsForm(prev => ({ ...prev, scheduled_at: e.target.value }))}
                        className="input-field"
                        min={new Date().toISOString().slice(0, 16)}
                      />
                      {newsForm.scheduled_at && (
                        <p className="text-xs text-yellow-400 mt-1">⏰ This post will go live on {new Date(newsForm.scheduled_at).toLocaleString()}</p>
                      )}
                    </div>

                    {/* SEO Section */}
                    <div className="md:col-span-2 border border-stroke-primary rounded-[4px] p-4 bg-surface-tertiary">
                      <div className="flex items-center gap-2 mb-3">
                        <span className="w-1 h-4 bg-brand-red inline-block rounded-sm flex-shrink-0" />
                        <label className="text-text-primary text-xs font-bold uppercase tracking-wide">SEO Settings</label>
                        <span className="text-text-muted text-xs font-normal normal-case ml-1">— override defaults for search engines</span>
                      </div>
                      <div className="grid grid-cols-1 gap-3">
                        <div>
                          <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                            Meta Title <span className="normal-case font-normal text-text-muted">(leave blank to use post title)</span>
                          </label>
                          <input
                            type="text"
                            value={newsForm.meta_title}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, meta_title: e.target.value }))}
                            placeholder={newsForm.title ? `${newsForm.title} | ViddStar News` : 'Custom SEO title...'}
                            maxLength={70}
                            className="input-field"
                          />
                          <p className="text-text-muted text-xs mt-1">{newsForm.meta_title.length}/70 characters — ideal: 50–60</p>
                        </div>
                        <div>
                          <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                            Meta Description <span className="normal-case font-normal text-text-muted">(leave blank to use excerpt)</span>
                          </label>
                          <textarea
                            value={newsForm.meta_description}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, meta_description: e.target.value }))}
                            placeholder={newsForm.excerpt || 'Custom SEO description shown in search results...'}
                            maxLength={160}
                            rows={2}
                            className="input-field resize-none"
                          />
                          <p className={`text-xs mt-1 ${newsForm.meta_description.length > 155 ? 'text-yellow-400' : 'text-text-muted'}`}>{newsForm.meta_description.length}/160 characters — ideal: 120–155</p>
                        </div>
                        <div>
                          <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                            OG / Social Share Image URL <span className="normal-case font-normal text-text-muted">(leave blank to use cover image)</span>
                          </label>
                          <input
                            type="text"
                            value={newsForm.og_image}
                            onChange={(e) => setNewsForm(prev => ({ ...prev, og_image: e.target.value }))}
                            placeholder="https://... (1200×630px recommended)"
                            className="input-field"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {newsFormError && (
                    <p className="text-brand-red text-sm mt-3">{newsFormError}</p>
                  )}

                  <div className="flex gap-3 mt-4 items-center flex-wrap">
                    <button
                      onClick={handleSaveNewsPost}
                      disabled={newsFormLoading}
                      className="btn-primary disabled:opacity-60"
                    >
                      {newsFormLoading ? 'Saving…' : editingPost ? 'Update Post' : 'Publish Post'}
                    </button>
                    <button
                      onClick={handleSaveDraft}
                      disabled={newsFormLoading}
                      className="px-4 py-2 border border-yellow-600 text-yellow-400 hover:bg-yellow-900 hover:text-yellow-200 text-sm font-bold uppercase rounded-[4px] transition-colors disabled:opacity-60"
                    >
                      {newsFormLoading ? 'Saving…' : 'Save as Draft'}
                    </button>
                    <button
                      onClick={() => { setShowNewsForm(false); setEditingPost(null); }}
                      className="px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary text-sm font-bold uppercase rounded-[4px] transition-colors"
                    >
                      Cancel
                    </button>
                    {/* Auto-save status indicator */}
                    {autoSaveStatus === 'saving' && (
                      <span className="flex items-center gap-1.5 text-text-muted text-xs ml-1">
                        <div className="w-3 h-3 border-2 border-text-muted border-t-transparent rounded-full animate-spin" />
                        Auto-saving draft…
                      </span>
                    )}
                    {autoSaveStatus === 'saved' && (
                      <span className="flex items-center gap-1.5 text-green-400 text-xs ml-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        Draft auto-saved
                      </span>
                    )}
                    {autoSaveStatus === 'error' && (
                      <span className="flex items-center gap-1.5 text-yellow-400 text-xs ml-1">
                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        Auto-save failed
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* News Posts List */}
              {newsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
                </div>
              ) : newsError ? (
                <div className="text-brand-red text-sm py-4">{newsError}</div>
              ) : newsPosts.length === 0 ? (
                <div className="text-center py-16 border border-stroke-primary border-dashed">
                  <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-text-secondary text-sm">No news posts yet. Create your first post!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {newsPosts.map(post => (
                    <div key={post.id} className="flex items-start gap-4 p-4 border border-stroke-primary bg-surface-secondary hover:border-stroke-light transition-colors">
                      {post.cover_image && (
                        <img src={post.cover_image} alt={post.cover_image_alt || post.title} className="w-20 h-14 object-cover flex-shrink-0 rounded-[2px]" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          {post.scheduled_at && new Date(post.scheduled_at) > new Date() ? (
                            <span className="text-xs font-bold uppercase px-1.5 py-0.5 rounded-[2px] bg-yellow-900 text-yellow-300">
                              Scheduled
                            </span>
                          ) : (
                            <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${post.is_published ? 'bg-green-900 text-green-300' : 'bg-surface-overlay text-text-muted'}`}>
                              {post.is_published ? 'Published' : 'Draft'}
                            </span>
                          )}
                          {post.is_featured && (
                            <span className="text-xs font-bold uppercase px-1.5 py-0.5 bg-brand-red text-white rounded-[2px]">Featured</span>
                          )}
                          <span className="text-text-muted text-xs">{post.category || 'News'}</span>
                          <span className="text-text-muted text-xs">·</span>
                          <span className="text-text-muted text-xs">By {post.author || 'ViddStar'}</span>
                          {post.scheduled_at && new Date(post.scheduled_at) > new Date() && (
                            <span className="text-yellow-400 text-xs">· Goes live {new Date(post.scheduled_at).toLocaleString()}</span>
                          )}
                        </div>
                        <h4 className="text-text-primary font-bold text-sm leading-snug line-clamp-1">{post.title}</h4>
                        {post.excerpt && <p className="text-text-secondary text-xs mt-1 line-clamp-2">{post.excerpt}</p>}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <button
                          onClick={() => handleToggleNewsPublished(post)}
                          className={`text-xs font-bold uppercase px-2 py-1 border rounded-[4px] transition-colors ${
                            post.is_published
                              ? 'border-stroke-primary text-text-secondary hover:border-brand-red hover:text-brand-red' :'border-green-700 text-green-400 hover:bg-green-900'
                          }`}
                          title={post.is_published ? 'Unpublish' : 'Publish'}
                        >
                          {post.is_published ? 'Unpublish' : 'Publish'}
                        </button>
                        <button
                          onClick={() => openNewsForm(post)}
                          className="text-text-secondary hover:text-text-primary transition-colors p-1"
                          title="Edit post"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-4.899a4 4 0 005.656 0l4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                          </svg>
                        </button>
                        <button
                          onClick={() => { setEditNewsViewsPost(post); setEditNewsViewsCount(post.views ?? 0); setEditNewsViewsError(''); }}
                          className="text-text-secondary hover:text-blue-400 transition-colors p-1"
                          title="Edit view count"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        {newsDeleteConfirmId === post.id ? (
                          <div className="flex items-center gap-1">
                            <button
                              onClick={() => handleDeleteNewsPost(post.id)}
                              disabled={newsDeleteLoading}
                              className="text-xs bg-brand-red text-white px-2 py-1 rounded-[4px] font-bold disabled:opacity-60"
                            >
                              {newsDeleteLoading ? '…' : 'Delete'}
                            </button>
                            <button
                              onClick={() => setNewsDeleteConfirmId(null)}
                              className="text-xs border border-stroke-primary text-text-secondary px-2 py-1 rounded-[4px]"
                            >
                              Cancel
                            </button>
                          </div>
                        ) : (
                          <button
                            onClick={() => setNewsDeleteConfirmId(post.id)}
                            className="text-text-secondary hover:text-brand-red transition-colors p-1"
                            title="Delete post"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── PENDING VIDEOS TAB ── */}
          {activeTab === 'pending' && (
            <div className="p-4">
              {pendingVideos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-text-secondary text-base">All caught up!</p>
                  <p className="text-text-muted text-sm">No videos pending review</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pendingVideos.map((video) => (
                    <div key={video.id} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] overflow-hidden">
                      <div className="flex gap-3 p-3">
                        <button
                          onClick={() => setPreviewVideo(video)}
                          className="flex-shrink-0 w-32 relative group focus:outline-none"
                          style={{ aspectRatio: '16/9' }}
                          title="Click to preview video"
                        >
                          <img src={video.thumbnail} alt={video.thumbnailAlt} className="w-full h-full object-cover group-hover:brightness-60 transition-all duration-200" />
                          <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                            <div className="w-9 h-9 bg-brand-red bg-opacity-90 rounded-full flex items-center justify-center shadow-lg">
                              <svg className="w-4 h-4 text-white ml-0.5" fill="currentColor" viewBox="0 0 24 24">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            </div>
                          </div>
                          <div className="absolute top-1 left-1">
                            <span className="pending-badge">Pending</span>
                          </div>
                        </button>
                        <div className="flex-1 min-w-0">
                          <h3 className="text-text-primary text-sm font-normal leading-snug mb-1 line-clamp-2">
                            {video.tag && <span className="tag-label mr-1 text-xs">{video.tag}</span>}
                            {video.title}
                          </h3>
                          <div className="flex flex-wrap gap-2 text-xs text-text-muted mb-2">
                            <span>by <span className="text-text-secondary">{video.submittedBy}</span></span>
                            <span>•</span>
                            <span>{video.submittedAt}</span>
                            <span>•</span>
                            <span className="capitalize">{video.source}</span>
                          </div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <button
                              onClick={() => setPreviewVideo(video)}
                              className="flex items-center gap-1 bg-surface-overlay hover:bg-surface-secondary border border-stroke-primary hover:border-stroke-light text-text-secondary hover:text-text-primary text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors duration-150"
                            >
                              Watch
                            </button>
                            <button
                              onClick={() => onApprove(video.id)}
                              className="flex items-center gap-1 bg-green-700 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors duration-150"
                            >
                              Approve
                            </button>
                            <button
                              onClick={() => onReject(video.id)}
                              className="flex items-center gap-1 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors duration-150"
                            >
                              Reject
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MANAGE VIDEOS TAB ── */}
          {activeTab === 'videos' && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-3 gap-3">
                <input
                  type="text"
                  value={videoSearchQuery}
                  onChange={(e) => setVideoSearchQuery(e.target.value)}
                  placeholder="Search by title, user, or tag…"
                  className="input-field flex-1 text-sm"
                />
                <button
                  onClick={fetchAllVideos}
                  disabled={videosLoading}
                  className="text-text-muted hover:text-text-secondary text-xs flex items-center gap-1 transition-colors disabled:opacity-50"
                >
                  <svg className={`w-3.5 h-3.5 ${videosLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 00-14 0 7 7 0 0014 0z" />
                  </svg>
                  Refresh
                </button>
              </div>

              <p className="text-text-muted text-xs uppercase tracking-wide mb-3">
                {videosLoading ? 'Loading…' : `${filteredVideos.length} video${filteredVideos.length !== 1 ? 's' : ''}`}
              </p>

              {videosError && (
                <div className="mb-4 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
                  {videosError}
                </div>
              )}

              {videosLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-text-muted text-sm">Loading videos…</p>
                </div>
              ) : filteredVideos.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.069A1 1 0 0016.138 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <p className="text-text-secondary text-base">No videos found</p>
                  <p className="text-text-muted text-sm">Add videos using the Add Video tab</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {filteredVideos.map((video) => (
                    <div key={video.id} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3">
                      <div className="flex items-center gap-3">
                        {/* Thumbnail */}
                        <div className="w-20 flex-shrink-0 rounded overflow-hidden" style={{ aspectRatio: '16/9' }}>
                          {video.thumbnail ? (
                            <img src={video.thumbnail} alt={video.thumbnail_alt || video.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-surface-overlay flex items-center justify-center">
                              <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0016.138 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-1.5 mb-0.5 flex-wrap">
                            {video.tag && <span className="tag-label text-xs">{video.tag}</span>}
                            <span className={`text-xs px-1.5 py-0.5 rounded font-bold uppercase ${
                              video.video_status === 'approved' ? 'bg-green-900 text-green-300' :
                              video.video_status === 'rejected'? 'bg-red-900 text-red-300' : 'bg-yellow-900 text-yellow-300'
                            }`}>
                              {video.video_status || 'pending'}
                            </span>
                            <span className="text-xs text-text-muted capitalize">{video.source}</span>
                          </div>
                          <p className="text-text-primary text-sm font-normal line-clamp-1">{video.title}</p>
                          <div className="flex items-center gap-2 text-xs text-text-muted mt-0.5 flex-wrap">
                            <span>by <span className="text-text-secondary">{video.submittedBy || 'Unknown'}</span></span>
                            {video.submittedAt && <><span>•</span><span>{video.submittedAt}</span></>}
                            {video.category && <><span>•</span><span>{video.category}</span></>}
                            {video.views !== undefined && <><span>•</span><span>{video.views} views</span></>}
                          </div>
                        </div>

                        {/* Delete action */}
                        <div className="flex-shrink-0 flex items-center gap-2">
                          <button
                            onClick={() => {
                              setEditVideoTarget(video);
                              setEditVideoForm({
                                title: video.title ?? '',
                                description: video.description ?? '',
                                tag: video.tag ?? '',
                                category: video.category ?? '',
                                video_status: video.video_status ?? 'pending',
                                thumbnail: video.thumbnail ?? '',
                                thumbnail_alt: video.thumbnail_alt ?? '',
                              });
                              setEditVideoError('');
                            }}
                            className="flex items-center gap-1 bg-surface-overlay hover:bg-green-700 border border-stroke-primary hover:border-green-600 text-text-secondary hover:text-white text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                            title="Edit video details"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
                            </svg>
                            Edit
                          </button>
                          <button
                            onClick={() => {
                              setEditStatsVideo(video);
                              setEditStatsForm({ views: video.views ?? 0, likes_count: video.likes_count ?? 0 });
                              setEditStatsViews(video.views ?? 0);
                              setEditStatsLikes(video.likes_count ?? 0);
                              setEditStatsError('');
                            }}
                            className="flex items-center gap-1 bg-surface-overlay hover:bg-blue-700 border border-stroke-primary hover:border-blue-600 text-text-secondary hover:text-white text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                            title="Edit views & likes"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Stats
                          </button>
                          <button
                            onClick={() => handleSetFeatured(video)}
                            disabled={featuredSetLoadingId === video.id}
                            title="Set as featured video"
                            className={`flex items-center gap-1 border text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors duration-150 disabled:opacity-60 ${
                              (featuredVideo?.id === video.id)
                                ? 'bg-yellow-600 border-yellow-500 text-white' :'bg-surface-overlay hover:bg-yellow-700 border-stroke-primary hover:border-yellow-600 text-text-secondary hover:text-white'
                            }`}
                          >
                            {featuredSetLoadingId === video.id ? (
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            )}
                            {featuredVideo?.id === video.id ? 'Featured' : 'Feature'}
                          </button>
                          {videoDeleteConfirmId === video.id ? (
                            <div className="flex items-center gap-1">
                              <span className="text-xs text-text-muted mr-1">Delete?</span>
                              <button
                                onClick={() => handleDeleteVideo(video.id)}
                                disabled={videoDeleteLoading}
                                className="text-xs font-bold px-2.5 py-1.5 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
                              >
                                {videoDeleteLoading ? '…' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setVideoDeleteConfirmId(null)}
                                className="text-xs font-bold px-2.5 py-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setVideoDeleteConfirmId(video.id)}
                              className="flex items-center gap-1 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                              title="Delete video"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ADD VIDEO TAB ── */}
          {activeTab === 'upload' && (
            <div className="p-5">
              <p className="text-text-muted text-xs uppercase tracking-wide mb-4">Add a video by uploading a file or submitting a YouTube / Vimeo link — it will be published immediately as approved.</p>

              {/* Source Type Toggle */}
              <div className="flex gap-1 mb-5 bg-surface-tertiary border border-stroke-primary rounded-[4px] p-1">
                {([
                  { key: 'file', label: '📁 File Upload' },
                  { key: 'youtube', label: '▶ YouTube' },
                  { key: 'vimeo', label: '🎬 Vimeo' },
                ] as { key: UploadSourceType; label: string }[]).map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => { setUploadSourceType(opt.key); setUploadError(''); setVideoUrl(''); setCustomThumbnailFile(null); setCustomThumbnailPreview(null); setAdminThumbnailOptions([]); setAdminSelectedThumbnail(''); setAdminShowThumbnailPicker(false); setAdminThumbnailLoadErrors({}); }}
                    className={`flex-1 py-2 text-xs font-bold uppercase tracking-wide rounded-[3px] transition-colors duration-150 ${
                      uploadSourceType === opt.key
                        ? 'bg-brand-red text-white' : 'text-text-secondary hover:text-text-primary'
                    }`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>

              <div className="space-y-4">
                {/* YouTube URL input */}
                {uploadSourceType === 'youtube' && (
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">YouTube URL <span className="text-brand-red">*</span></label>
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={(e) => { setVideoUrl(e.target.value); setAdminShowThumbnailPicker(false); setAdminThumbnailOptions([]); setAdminSelectedThumbnail(''); }}
                      placeholder="https://www.youtube.com/watch?v=… or https://youtu.be/…"
                      className="input-field"
                    />
                    {videoUrl && extractYouTubeId(videoUrl) && (
                      <div className="mt-2 rounded-[4px] overflow-hidden border border-stroke-primary" style={{ aspectRatio: '16/9' }}>
                        <iframe
                          src={`https://www.youtube.com/embed/${extractYouTubeId(videoUrl)}?rel=0`}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title="YouTube preview"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Vimeo URL input */}
                {uploadSourceType === 'vimeo' && (
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Vimeo URL <span className="text-brand-red">*</span></label>
                    <input
                      type="url"
                      value={videoUrl}
                      onChange={(e) => { setVideoUrl(e.target.value); setAdminShowThumbnailPicker(false); setAdminThumbnailOptions([]); setAdminSelectedThumbnail(''); }}
                      placeholder="https://vimeo.com/…"
                      className="input-field"
                    />
                    {videoUrl && extractVimeoId(videoUrl) && (
                      <div className="mt-2 rounded-[4px] overflow-hidden border border-stroke-primary" style={{ aspectRatio: '16/9' }}>
                        <iframe
                          src={`https://player.vimeo.com/video/${extractVimeoId(videoUrl)}`}
                          className="w-full h-full"
                          allow="autoplay; fullscreen; picture-in-picture"
                          allowFullScreen
                          title="Vimeo preview"
                        />
                      </div>
                    )}
                  </div>
                )}

                {/* Thumbnail Picker for YouTube / Vimeo */}
                {(uploadSourceType === 'youtube' || uploadSourceType === 'vimeo') && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide">
                        Thumbnail Preview <span className="text-brand-red">*</span>
                      </label>
                      {!adminShowThumbnailPicker && (
                        <button
                          type="button"
                          onClick={handleLoadAdminThumbnails}
                          className="flex items-center gap-1.5 text-xs font-bold uppercase tracking-wide text-brand-red hover:text-red-400 transition-colors duration-150"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          Load Thumbnails
                        </button>
                      )}
                      {adminShowThumbnailPicker && (
                        <button
                          type="button"
                          onClick={() => { setAdminShowThumbnailPicker(false); setAdminThumbnailOptions([]); setAdminSelectedThumbnail(''); }}
                          className="text-xs text-text-muted hover:text-text-secondary transition-colors duration-150"
                        >
                          Reset
                        </button>
                      )}
                    </div>

                    {adminShowThumbnailPicker && adminThumbnailOptions.length > 0 && (
                      <div className="space-y-3">
                        <p className="text-text-muted text-xs">Click a thumbnail to select it as the video cover image.</p>
                        <div className="grid grid-cols-2 gap-2">
                          {adminThumbnailOptions.map((option, index) => {
                            const isSelected = adminSelectedThumbnail === option.url;
                            const hasError = adminThumbnailLoadErrors[index];
                            return (
                              <button
                                key={index}
                                type="button"
                                onClick={() => setAdminSelectedThumbnail(option.url)}
                                className={`relative rounded-[4px] overflow-hidden border-2 transition-all duration-150 focus:outline-none ${
                                  isSelected
                                    ? 'border-brand-red ring-2 ring-brand-red ring-opacity-40' :'border-stroke-primary hover:border-stroke-light'
                                }`}
                              >
                                {hasError ? (
                                  <div className="w-full h-24 bg-surface-tertiary flex flex-col items-center justify-center gap-1">
                                    <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                    </svg>
                                    <span className="text-text-muted text-xs">Not available</span>
                                  </div>
                                ) : (
                                  <img
                                    src={option.url}
                                    alt={`Thumbnail option: ${option.label}`}
                                    className="w-full h-24 object-cover"
                                    onError={() => setAdminThumbnailLoadErrors(prev => ({ ...prev, [index]: true }))}
                                  />
                                )}
                                <div className={`px-2 py-1 text-xs font-semibold text-center transition-colors duration-150 ${
                                  isSelected ? 'bg-brand-red text-white' : 'bg-surface-secondary text-text-secondary'
                                }`}>
                                  {option.label}
                                </div>
                                {isSelected && (
                                  <div className="absolute top-1 right-1 w-5 h-5 bg-brand-red rounded-full flex items-center justify-center shadow">
                                    <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </div>
                                )}
                              </button>
                            );
                          })}
                        </div>
                        {adminSelectedThumbnail && (
                          <div className="mt-2">
                            <p className="text-text-muted text-xs uppercase tracking-wide mb-1.5">Selected Thumbnail</p>
                            <div className="relative rounded-[4px] overflow-hidden border border-brand-red border-opacity-50">
                              <img
                                src={adminSelectedThumbnail}
                                alt="Selected thumbnail"
                                className="w-full h-36 object-cover"
                              />
                              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent px-3 py-2">
                                <p className="text-white text-xs font-semibold truncate">{uploadForm.title || 'Video Title'}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    )}

                    {!adminShowThumbnailPicker && (
                      <p className="text-text-muted text-xs bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2">
                        Enter the video URL above, fill in the title and tag, then click <span className="text-text-secondary font-semibold">Load Thumbnails</span> to preview and choose a thumbnail before publishing.
                      </p>
                    )}
                  </div>
                )}

                {/* File picker */}
                {uploadSourceType === 'file' && (
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Video File <span className="text-brand-red">*</span></label>
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-[4px] p-5 text-center cursor-pointer transition-colors duration-150 ${
                        uploadFile ? 'border-brand-red bg-brand-red/5' : 'border-stroke-primary hover:border-stroke-light'
                      }`}
                    >
                      {uploadPreviewUrl ? (
                        <div className="space-y-2">
                          <video src={uploadPreviewUrl} className="w-full max-h-40 rounded object-contain mx-auto" controls />
                          <p className="text-text-secondary text-xs truncate">{uploadFile?.name}</p>
                          <p className="text-text-muted text-xs mt-1">MP4, WebM, MOV, AVI — up to 500MB</p>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setUploadFile(null); setUploadPreviewUrl(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                            className="text-brand-red text-xs hover:underline"
                          >
                            Remove file
                          </button>
                        </div>
                      ) : (
                        <div className="py-4">
                          <svg className="w-10 h-10 text-text-muted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0016.138 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                          <p className="text-text-secondary text-sm">Click to select a video file</p>
                          <p className="text-text-muted text-xs mt-1">MP4, WebM, MOV, AVI — up to 500MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime,video/x-msvideo,video/avi,video/ogg,video/3gpp,video/x-matroska"
                      onChange={handleFileChange}
                      className="hidden"
                    />
                  </div>
                )}

                {/* Title */}
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Title <span className="text-brand-red">*</span></label>
                  <input
                    type="text"
                    value={uploadForm.title}
                    onChange={(e) => setUploadForm(p => ({ ...p, title: e.target.value }))}
                    placeholder="Enter video title…"
                    className="input-field"
                  />
                </div>

                {/* Tag + Category row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Tag <span className="text-brand-red">*</span></label>
                    <input
                      type="text"
                      value={uploadForm.tag}
                      onChange={(e) => setUploadForm(p => ({ ...p, tag: e.target.value }))}
                      placeholder="e.g. WILD, VIRAL"
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Category</label>
                    <select
                      value={uploadForm.category}
                      onChange={(e) => setUploadForm(p => ({ ...p, category: e.target.value }))}
                      className="input-field"
                    >
                      {VIDEO_CATEGORIES.map(cat => (
                        <option key={cat} value={cat}>{cat}</option>
                      ))}
                    </select>
                  </div>
                </div>

                {/* Description */}
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Description</label>
                  <textarea
                    value={uploadForm.description}
                    onChange={(e) => setUploadForm(p => ({ ...p, description: e.target.value }))}
                    placeholder="Short description of the video…"
                    rows={3}
                    className="input-field resize-none"
                  />
                </div>

                {/* Submitted By — editable, no default so admin can type any username */}
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                    Submitted By <span className="text-text-muted font-normal normal-case">(leave blank to use "Admin")</span>
                  </label>
                  <input
                    type="text"
                    value={uploadForm.submittedBy}
                    onChange={(e) => setUploadForm(p => ({ ...p, submittedBy: e.target.value }))}
                    placeholder="Enter username or leave blank for Admin"
                    className="input-field"
                  />
                </div>

                {/* Thumbnail URL + Alt row */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                      Thumbnail URL <span className="text-text-muted font-normal normal-case">(optional{uploadSourceType === 'youtube' ? ', auto-filled' : ''})</span>
                    </label>
                    <input
                      type="text"
                      value={uploadForm.thumbnail}
                      onChange={(e) => setUploadForm(p => ({ ...p, thumbnail: e.target.value }))}
                      placeholder="https://..."
                      className="input-field"
                    />
                  </div>
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Thumbnail Alt Text</label>
                    <input
                      type="text"
                      value={uploadForm.thumbnailAlt}
                      onChange={(e) => setUploadForm(p => ({ ...p, thumbnailAlt: e.target.value }))}
                      placeholder="Describe the thumbnail…"
                      className="input-field"
                    />
                  </div>
                </div>

                {/* Custom Thumbnail Upload */}
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                    Custom Thumbnail <span className="text-text-muted font-normal normal-case">(optional)</span>
                  </label>
                  <div
                    onClick={() => thumbnailInputRef.current?.click()}
                    className={`border-2 border-dashed rounded-[4px] p-4 text-center cursor-pointer transition-colors ${
                      customThumbnailFile ? 'border-brand-red bg-brand-red/5' : 'border-stroke-primary hover:border-stroke-light'
                    }`}
                  >
                    {customThumbnailPreview ? (
                      <div className="space-y-2">
                        <img src={customThumbnailPreview} alt="Custom thumbnail preview" className="max-h-32 mx-auto object-contain rounded" />
                        <p className="text-text-secondary text-xs truncate">{customThumbnailFile?.name}</p>
                        <p className="text-text-muted text-xs mt-1">JPG, PNG, GIF, WebP — up to 50MB</p>
                        <button
                          type="button"
                          onClick={(e) => { e.stopPropagation(); setCustomThumbnailFile(null); setCustomThumbnailPreview(null); if (thumbnailInputRef.current) thumbnailInputRef.current.value = ''; }}
                          className="text-brand-red text-xs hover:underline"
                        >
                          Remove
                        </button>
                      </div>
                    ) : (
                      <div className="py-3">
                        <svg className="w-8 h-8 text-text-muted mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.553-4.553a2 2 0 012.827 0L11 16m-2-2l1.586-1.586a2 2 0 012.827 0L19 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                        <p className="text-text-secondary text-sm">Click to upload custom thumbnail</p>
                        <p className="text-text-muted text-xs mt-0.5">JPG, PNG, GIF, WebP — up to 50MB</p>
                      </div>
                    )}
                  </div>
                  <input
                    ref={thumbnailInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                    onChange={(e) => {
                      const file = e.target.files?.[0] || null;
                      if (file && file.size > 52428800) {
                        setUploadError('Thumbnail file is too large. Maximum size is 50MB.');
                        if (thumbnailInputRef.current) thumbnailInputRef.current.value = '';
                        return;
                      }
                      setCustomThumbnailFile(file);
                      if (file) {
                        const url = URL.createObjectURL(file);
                        setCustomThumbnailPreview(url);
                      } else {
                        setCustomThumbnailPreview(null);
                      }
                    }}
                    className="hidden"
                  />
                </div>

                {/* Upload progress — file only */}
                {uploadSourceType === 'file' && uploadLoading && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-text-muted text-xs">Uploading…</span>
                      <span className="text-text-muted text-xs">{uploadProgress}%</span>
                    </div>
                    <div className="w-full bg-surface-tertiary rounded-full h-1.5">
                      <div className="bg-brand-red h-1.5 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                    </div>
                  </div>
                )}

                {uploadError && <p className="text-brand-red text-sm">{uploadError}</p>}

                {/* Submit button */}
                {uploadSourceType === 'file' ? (
                  <button
                    onClick={handleUploadVideo}
                    disabled={uploadLoading || !uploadFile}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                  >
                    {uploadLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Uploading… {uploadProgress}%
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Upload & Publish Video
                      </span>
                    )}
                  </button>
                ) : (
                  <button
                    onClick={handleSubmitUrlVideo}
                    disabled={uploadLoading}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                  >
                    {uploadLoading ? (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Publishing…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                        </svg>
                        Add {uploadSourceType === 'youtube' ? 'YouTube' : 'Vimeo'} Video
                      </span>
                    )}
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── USERS TAB ── */}
          {activeTab === 'users' && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <p className="text-text-muted text-xs uppercase tracking-wide">
                  {usersLoading ? 'Loading…' : `${users.length} registered user${users.length !== 1 ? 's' : ''}`}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    onClick={handleSignOutAllUsers}
                    disabled={signOutAllLoading}
                    className="flex items-center gap-1.5 bg-red-900/30 hover:bg-red-900/50 border border-red-700/50 hover:border-red-600 text-red-400 hover:text-red-300 text-xs font-bold uppercase tracking-wide px-3 py-1.5 rounded-[4px] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {signOutAllLoading ? (
                      <span className="w-3.5 h-3.5 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    )}
                    Sign Out All
                  </button>
                  <button
                    onClick={fetchUsers}
                    disabled={usersLoading}
                    className="text-text-muted hover:text-text-secondary text-xs flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-3.5 h-3.5 ${usersLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>
              </div>

              {signOutAllResult && (
                <div className={`mb-4 px-4 py-2.5 border rounded-[4px] text-sm flex items-center justify-between gap-2 ${signOutAllResult.success ? 'bg-green-900/30 border-green-700 text-green-400' : 'bg-red-900/30 border-red-700 text-red-400'}`}>
                  <span>{signOutAllResult.message}</span>
                  <button onClick={() => setSignOutAllResult(null)} className="opacity-60 hover:opacity-100 transition-opacity flex-shrink-0">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {usersError && (
                <div className="mb-4 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
                  {usersError}
                </div>
              )}

              {usersLoading ? (
                <div className="text-center py-12">
                  <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-text-muted text-sm">Loading users…</p>
                </div>
              ) : users.length === 0 ? (
                <div className="text-center py-12">
                  <svg className="w-12 h-12 text-text-muted mx-auto mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <p className="text-text-secondary text-base">No users found</p>
                  <p className="text-text-muted text-sm">Users will appear here once they register</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {users.map((user) => (
                    <div key={user.id} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.full_name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white text-sm font-bold uppercase">
                              {(user.full_name || user.email || 'U').charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="text-text-primary text-sm font-bold truncate">{user.full_name || 'No name'}</p>
                            {user.username && <span className="text-text-muted text-xs">@{user.username}</span>}
                          </div>
                          <p className="text-text-muted text-xs truncate">{user.email}</p>
                          {user.created_at && (
                            <p className="text-text-muted text-xs mt-0.5">
                              Joined {new Date(user.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                          <button
                            onClick={() => openEditUser(user)}
                            className="flex items-center gap-1 bg-surface-overlay hover:bg-surface-secondary border border-stroke-primary hover:border-stroke-light text-text-secondary hover:text-text-primary text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors duration-150"
                            title="Edit user"
                          >
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-4.899a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          {deleteConfirmId === user.id ? (
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => handleDeleteUser(user.id)}
                                disabled={deleteLoading}
                                className="text-xs font-bold px-2.5 py-1.5 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
                              >
                                {deleteLoading ? '…' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setDeleteConfirmId(null)}
                                className="text-xs border border-stroke-primary text-text-secondary hover:text-text-primary px-2 py-1 rounded-[4px] transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setDeleteConfirmId(user.id)}
                              className="flex items-center gap-1 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                            >
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MANAGE ADMINS TAB ── */}
          {activeTab === 'admins' && (
            <div className="p-4 space-y-5">

              {/* Search to grant admin */}
              <div>
                <p className="text-text-muted text-xs uppercase tracking-wide mb-2">Grant Admin Access</p>
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={adminSearchQuery}
                    onChange={(e) => setAdminSearchQuery(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAdminSearch(); }}
                    placeholder="Search by email, username, or name…"
                    className="input-field flex-1 text-sm"
                  />
                  <button
                    onClick={handleAdminSearch}
                    disabled={adminSearchLoading || !adminSearchQuery.trim()}
                    className="btn-primary px-4 disabled:opacity-50 flex-shrink-0"
                  >
                    {adminSearchLoading ? (
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 00-14 0 7 7 0 0014 0z" />
                      </svg>
                    )}
                  </button>
                </div>
                {adminSearchError && <p className="text-brand-red text-xs mt-2">{adminSearchError}</p>}

                {/* Search results */}
                {adminSearchResults.length > 0 && (
                  <div className="mt-3 space-y-2">
                    {adminSearchResults.map((user) => (
                      <div key={user.id} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.full_name || user.email} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white text-sm font-bold uppercase">
                              {(user.full_name || user.email || 'U').charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-text-primary text-sm font-bold truncate">{user.full_name || '—'}</p>
                          <p className="text-text-muted text-xs truncate">{user.email}</p>
                          {user.username && <p className="text-text-muted text-xs">@{user.username}</p>}
                        </div>
                        {user.is_admin ? (
                          <span className="text-xs font-bold text-brand-red border border-brand-red px-2 py-0.5 rounded-[4px] flex-shrink-0">Admin</span>
                        ) : (
                          <button
                            onClick={() => handleToggleAdmin(user, true)}
                            disabled={adminToggleLoadingId === user.id}
                            className="flex-shrink-0 flex items-center gap-1.5 bg-green-700 hover:bg-green-600 text-white text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors disabled:opacity-50"
                          >
                            {adminToggleLoadingId === user.id ? (
                              <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            ) : (
                              <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                              </svg>
                            )}
                            Grant Admin
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                )}
                {adminSearchResults.length === 0 && adminSearchQuery && !adminSearchLoading && (
                  <p className="text-text-muted text-xs mt-2">No results. Try a different search term.</p>
                )}
              </div>

              {/* Divider */}
              <div className="border-t border-stroke-primary" />

              {/* Current admins list */}
              <div>
                <div className="flex items-center justify-between mb-3">
                  <p className="text-text-muted text-xs uppercase tracking-wide">Current Admins</p>
                  <button
                    onClick={fetchAdmins}
                    disabled={adminsLoading}
                    className="text-text-muted hover:text-text-secondary text-xs flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-3.5 h-3.5 ${adminsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>

                {adminsError && (
                  <div className="mb-3 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
                    {adminsError}
                  </div>
                )}

                {adminsLoading ? (
                  <div className="text-center py-8">
                    <div className="w-7 h-7 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-2" />
                    <p className="text-text-muted text-sm">Loading admins…</p>
                  </div>
                ) : adminUsers.length === 0 ? (
                  <div className="text-center py-8">
                    <svg className="w-10 h-10 text-text-muted mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <p className="text-text-secondary text-sm">No admins found</p>
                    <p className="text-text-muted text-xs">Search for a user above to grant admin access</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {adminUsers.map((user) => (
                      <div key={user.id} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3 flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-brand-red flex items-center justify-center flex-shrink-0 overflow-hidden">
                          {user.avatar_url ? (
                            <img src={user.avatar_url} alt={user.full_name || user.email} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-white text-sm font-bold uppercase">
                              {(user.full_name || user.email || 'U').charAt(0)}
                            </span>
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-text-primary text-sm font-bold truncate">{user.full_name || '—'}</p>
                            <span className="text-xs font-bold text-brand-red border border-brand-red px-1.5 py-0.5 rounded-[4px] flex-shrink-0">Admin</span>
                          </div>
                          <p className="text-text-muted text-xs truncate">{user.email}</p>
                          {user.username && <p className="text-text-muted text-xs">@{user.username}</p>}
                        </div>
                        <button
                          onClick={() => handleToggleAdmin(user, false)}
                          disabled={adminToggleLoadingId === user.id}
                          className="flex-shrink-0 flex items-center gap-1.5 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors disabled:opacity-50"
                        >
                          {adminToggleLoadingId === user.id ? (
                            <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                          ) : (
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                          )}
                          Revoke
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── FEATURED VIDEO TAB ── */}
          {activeTab === 'featured' && (
            <div className="p-4">
              <p className="text-text-muted text-xs uppercase tracking-wide mb-4">
                Choose one video to be pinned as the featured video at the top of the homepage. Only one video can be featured at a time.
              </p>

              {featuredError && (
                <div className="mb-4 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
                  {featuredError}
                </div>
              )}

              {/* Current featured video */}
              <div className="mb-5">
                <h3 className="text-text-muted text-xs uppercase tracking-wide mb-2">Currently Featured</h3>
                {featuredLoading ? (
                  <div className="flex items-center gap-2 py-3">
                    <div className="w-5 h-5 border-2 border-brand-red border-t-transparent rounded-full animate-spin"></div>
                    <span className="text-text-muted text-sm">Loading…</span>
                  </div>
                ) : featuredVideo ? (
                  <div className="bg-yellow-900/20 border border-yellow-700 rounded-[4px] p-3 flex items-center gap-3">
                    <div className="w-20 flex-shrink-0 rounded overflow-hidden" style={{ aspectRatio: '16/9' }}>
                      {featuredVideo.thumbnail ? (
                        <img src={featuredVideo.thumbnail} alt={featuredVideo.thumbnail_alt || featuredVideo.title} className="w-full h-full object-cover" />
                      ) : (
                        <div className="w-full h-full bg-surface-overlay flex items-center justify-center">
                          <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0016.138 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                          </svg>
                        </div>
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <svg className="w-3.5 h-3.5 text-yellow-400 flex-shrink-0" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                        </svg>
                        {featuredVideo.tag && <span className="tag-label text-xs">{featuredVideo.tag}</span>}
                      </div>
                      <p className="text-text-primary text-sm font-normal line-clamp-1">{featuredVideo.title}</p>
                      <p className="text-text-muted text-xs mt-0.5">by {featuredVideo.submitted_by || 'Unknown'}</p>
                    </div>
                    <button
                      onClick={handleUnsetFeatured}
                      disabled={featuredSetLoadingId === featuredVideo.id}
                      className="flex-shrink-0 flex items-center gap-1.5 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors disabled:opacity-50"
                    >
                      {featuredSetLoadingId === featuredVideo.id ? (
                        <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                      ) : 'Remove'}
                    </button>
                  </div>
                ) : (
                  <div className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3 text-text-muted text-sm">
                    No featured video set. The homepage will auto-rotate recent videos.
                  </div>
                )}
              </div>

              {/* Search & pick from approved videos */}
              <h3 className="text-text-muted text-xs uppercase tracking-wide mb-2">Pick a Video to Feature</h3>
              <input
                type="text"
                value={featuredSearchQuery}
                onChange={(e) => setFeaturedSearchQuery(e.target.value)}
                placeholder="Search approved videos…"
                className="input-field mb-3 text-sm"
              />

              {videosLoading ? (
                <div className="text-center py-8">
                  <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
                  <p className="text-text-muted text-sm">Loading videos…</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-72 overflow-y-auto pr-1">
                  {allVideos
                    .filter(v => v.video_status === 'approved')
                    .filter(v =>
                      !featuredSearchQuery ||
                      v.title?.toLowerCase().includes(featuredSearchQuery.toLowerCase()) ||
                      v.tag?.toLowerCase().includes(featuredSearchQuery.toLowerCase()) ||
                      v.submitted_by?.toLowerCase().includes(featuredSearchQuery.toLowerCase())
                    )
                    .map((video) => {
                      const isCurrent = featuredVideo?.id === video.id;
                      return (
                        <div
                          key={video.id}
                          className={`flex items-center gap-3 p-3 rounded-[4px] border transition-colors ${
                            isCurrent
                              ? 'bg-yellow-900/20 border-yellow-700' :'bg-surface-tertiary border-stroke-primary'
                          }`}
                        >
                          <div className="w-16 flex-shrink-0 rounded overflow-hidden" style={{ aspectRatio: '16/9' }}>
                            {video.thumbnail ? (
                              <img src={video.thumbnail} alt={video.thumbnail_alt || video.title} className="w-full h-full object-cover" />
                            ) : (
                              <div className="w-full h-full bg-surface-overlay flex items-center justify-center">
                                <svg className="w-4 h-4 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0016.138 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                </svg>
                              </div>
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            {video.tag && <span className="tag-label text-xs mr-1">{video.tag}</span>}
                            <p className="text-text-primary text-sm line-clamp-1">{video.title}</p>
                            <p className="text-text-muted text-xs mt-0.5">by {video.submitted_by || 'Unknown'}</p>
                          </div>
                          <button
                            onClick={() => handleSetFeatured(video)}
                            disabled={isCurrent || featuredSetLoadingId === video.id}
                            className={`flex-shrink-0 flex items-center gap-1.5 text-xs font-bold px-3 py-1.5 rounded-[4px] transition-colors disabled:opacity-60 ${
                              isCurrent
                                ? 'bg-yellow-600 border border-yellow-500 text-white cursor-default' :'bg-surface-overlay hover:bg-yellow-700 border border-stroke-primary hover:border-yellow-600 text-text-secondary hover:text-white'
                            }`}
                          >
                            {featuredSetLoadingId === video.id ? (
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : isCurrent ? (
                              <>
                                <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24">
                                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                                </svg>
                                Featured
                              </>
                            ) : 'Set Featured'}
                          </button>
                        </div>
                      );
                    })}
                  {allVideos.filter(v => v.video_status === 'approved').length === 0 && (
                    <p className="text-text-muted text-sm text-center py-6">No approved videos available.</p>
                  )}
                </div>
              )}
            </div>
          )}

          {/* ── ADS MANAGEMENT TAB ── */}
          {activeTab === 'ads' && (
            <div className="p-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-text-primary text-sm font-bold uppercase tracking-wide">Ad Creatives</p>
                  <p className="text-text-muted text-xs mt-0.5">Upload ads, set placements, and control which are active</p>
                </div>
                <button
                  onClick={() => { if (showAdForm) { setShowAdForm(false); setEditingAd(null); setAdFormError(''); } else { openAdForm(); } }}
                  className="btn-primary text-sm"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  {showAdForm ? 'Cancel' : 'New Ad'}
                </button>
              </div>

              {adsError && (
                <div className="mb-4 px-4 py-2.5 bg-red-900/30 border border-red-700 rounded-[4px] text-red-400 text-sm">
                  {adsError}
                </div>
              )}

              {/* ── New Ad Form ── */}
              {showAdForm && (
                <div className="mb-5 bg-surface-tertiary border border-stroke-primary rounded-[4px] p-4 space-y-4">
                  <h3 className="text-text-primary font-bold uppercase text-sm tracking-wide border-b border-stroke-primary pb-2">{editingAd ? 'Edit Ad Creative' : 'New Ad Creative'}</h3>

                  {/* Image upload */}
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">
                      Ad Image <span className="text-brand-red">*</span>
                    </label>
                    <div
                      onClick={() => adImageInputRef.current?.click()}
                      className={`border-2 border-dashed rounded-[4px] p-4 text-center cursor-pointer transition-colors ${
                        adImageFile ? 'border-brand-red bg-brand-red/5' : 'border-stroke-primary hover:border-stroke-light'
                      }`}
                    >
                      {adImagePreview ? (
                        <div className="space-y-2">
                          <img src={adImagePreview} alt="Ad preview" className="max-h-32 mx-auto object-contain rounded" />
                          <p className="text-text-secondary text-xs truncate">{adImageFile?.name}</p>
                          <p className="text-text-muted text-xs mt-1">JPG, PNG, GIF, WebP — up to 10MB</p>
                          <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); setAdImageFile(null); setAdImagePreview(null); if (adImageInputRef.current) adImageInputRef.current.value = ''; }}
                            className="text-brand-red text-xs hover:underline"
                          >
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div className="py-3">
                          <svg className="w-8 h-8 text-text-muted mx-auto mb-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.553-4.553a2 2 0 012.827 0L11 16m-2-2l1.586-1.586a2 2 0 012.827 0L19 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                          </svg>
                          <p className="text-text-secondary text-sm">Click to upload image</p>
                          <p className="text-text-muted text-xs mt-0.5">JPG, PNG, GIF, WebP — up to 10MB</p>
                        </div>
                      )}
                    </div>
                    <input
                      ref={adImageInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp,image/svg+xml"
                      onChange={handleAdImageChange}
                      className="hidden"
                    />
                    <p className="text-text-muted text-xs mt-1.5">Or paste an image URL below:</p>
                    <input
                      type="url"
                      value={adForm.image_url}
                      onChange={(e) => setAdForm(p => ({ ...p, image_url: e.target.value }))}
                      placeholder="https://..."
                      className="input-field mt-1.5 text-sm"
                      disabled={!!adImageFile}
                    />
                  </div>

                  {/* Title + Advertiser */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Ad Title <span className="text-brand-red">*</span></label>
                      <input
                        type="text"
                        value={adForm.title}
                        onChange={(e) => setAdForm(p => ({ ...p, title: e.target.value }))}
                        placeholder="e.g. Summer Sale 50% Off"
                        className="input-field text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Advertiser</label>
                      <input
                        type="text"
                        value={adForm.advertiser}
                        onChange={(e) => setAdForm(p => ({ ...p, advertiser: e.target.value }))}
                        placeholder="Company or brand name"
                        className="input-field text-sm"
                      />
                    </div>
                  </div>

                  {/* Image Alt */}
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Image Alt Text</label>
                    <input
                      type="text"
                      value={adForm.image_alt}
                      onChange={(e) => setAdForm(p => ({ ...p, image_alt: e.target.value }))}
                      placeholder="Describe the ad image for accessibility"
                      className="input-field text-sm"
                    />
                  </div>

                  {/* Link URL + Placement */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Destination URL <span className="text-brand-red">*</span></label>
                      <input
                        type="url"
                        value={adForm.link_url}
                        onChange={(e) => setAdForm(p => ({ ...p, link_url: e.target.value }))}
                        placeholder="https://advertiser.com/landing"
                        className="input-field text-sm"
                      />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Placement</label>
                      <select
                        value={adForm.placement}
                        onChange={(e) => setAdForm(p => ({ ...p, placement: e.target.value }))}
                        className="input-field text-sm"
                      >
                        <option value="banner">Banner (top of page)</option>
                        <option value="sidebar">Sidebar</option>
                        <option value="between-videos">Between Videos</option>
                        <option value="video-post">Video Post Page</option>
                        <option value="blog-post">Blog Post Page</option>
                      </select>
                    </div>
                  </div>

                  {/* Notes */}
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Internal Notes</label>
                    <input
                      type="text"
                      value={adForm.notes}
                      onChange={(e) => setAdForm(p => ({ ...p, notes: e.target.value }))}
                      placeholder="e.g. Paid $500 for 30 days, contact: john@brand.com"
                      className="input-field text-sm"
                    />
                  </div>

                  {adFormError && <p className="text-brand-red text-sm">{adFormError}</p>}

                  <button
                    onClick={handleSaveAd}
                    disabled={adFormLoading}
                    className="btn-primary w-full justify-center disabled:opacity-60"
                  >
                    {adFormLoading ? (
                      <span className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Saving…
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        {editingAd ? 'Update Ad' : 'Save & Activate Ad'}
                      </span>
                    )}
                  </button>
                </div>
              )}

              {/* ── Placement legend ── */}
              <div className="mb-4 grid grid-cols-3 gap-2">
                {[
                  { key: 'banner', label: 'Banner', desc: 'Top of page' },
                  { key: 'sidebar', label: 'Sidebar', desc: 'Right sidebar' },
                  { key: 'between-videos', label: 'Between Videos', desc: 'In video grid' },
                  { key: 'video-post', label: 'Video Post', desc: 'Video post pages' },
                  { key: 'blog-post', label: 'Blog Post', desc: 'Blog/news post pages' },
                ].map((p) => {
                  const count = ads.filter(a => a.placement === p.key).length;
                  const active = ads.filter(a => a.placement === p.key && a.is_active).length;
                  return (
                    <div key={p.key} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2 text-center">
                      <p className="text-text-primary text-xs font-bold uppercase">{p.label || p.key}</p>
                      <p className="text-text-muted text-xs">{p.desc}</p>
                      <p className="text-brand-red text-sm font-bold mt-1">{active}<span className="text-text-muted font-normal">/{count}</span></p>
                      <p className="text-text-muted text-xs">active</p>
                    </div>
                  );
                })}
              </div>

              {/* ── Ads list ── */}
              {adsLoading ? (
                <div className="text-center py-10">
                  <div className="w-8 h-8 border-2 border-brand-red border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                  <p className="text-text-muted text-sm">Loading ads…</p>
                </div>
              ) : ads.length === 0 ? (
                <div className="text-center py-10 text-text-muted text-sm">
                  <p className="text-text-muted text-sm">Click "New Ad" to create your first ad placement</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {ads.map((ad) => (
                    <div
                      key={ad.id}
                      className={`border rounded-[4px] overflow-hidden transition-colors ${
                        ad.is_active ? 'border-stroke-primary bg-surface-tertiary' : 'border-stroke-secondary bg-surface-primary opacity-60'
                      }`}
                    >
                      <div className="flex items-center gap-3 p-3">
                        {/* Thumbnail */}
                        <div className="w-24 flex-shrink-0 rounded overflow-hidden border border-stroke-secondary" style={{ aspectRatio: '16/9' }}>
                          {ad.image_url ? (
                            <img src={ad.image_url} alt={ad.image_alt || ad.title} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full bg-surface-overlay flex items-center justify-center">
                              <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.553-4.553a2 2 0 012.827 0L11 16m-2-2l1.586-1.586a2 2 0 012.827 0L19 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                              </svg>
                            </div>
                          )}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className={`text-xs font-bold uppercase px-1.5 py-0.5 rounded-[2px] ${ad.is_active ? 'bg-green-900 text-green-300' : 'bg-surface-overlay text-text-muted'}`}>
                              {ad.is_active ? 'Active' : 'Paused'}
                            </span>
                            <span className="text-xs text-text-muted border border-stroke-secondary px-1.5 py-0.5 rounded capitalize">
                              {ad.placement === 'between-videos' ? 'Between Videos' : ad.placement === 'video-post' ? 'Video Post' : ad.placement === 'blog-post' ? 'Blog Post' : ad.placement}
                            </span>
                          </div>
                          <p className="text-text-primary text-sm font-bold line-clamp-1">{ad.title}</p>
                          {ad.advertiser && <p className="text-text-muted text-xs">{ad.advertiser}</p>}
                          {ad.notes && <p className="text-text-muted text-xs italic mt-0.5 line-clamp-1">{ad.notes}</p>}
                          <a
                            href={ad.link_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-brand-red text-xs hover:underline truncate block mt-0.5"
                          >
                            {ad.link_url}
                          </a>
                        </div>

                        {/* Actions */}
                        <div className="flex flex-col gap-1.5 flex-shrink-0 flex-wrap justify-end">
                          {/* Edit */}
                          <button
                            onClick={() => openAdForm(ad)}
                            className="flex items-center gap-1 bg-surface-overlay hover:bg-surface-tertiary border border-stroke-primary text-text-secondary hover:text-text-primary text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                            title="Edit ad"
                          >
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-4.899a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                            Edit
                          </button>
                          {/* Toggle active */}
                          <button
                            onClick={() => handleToggleAd(ad)}
                            disabled={adToggleLoadingId === ad.id}
                            className={`flex items-center gap-1 text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors disabled:opacity-60 ${
                              ad.is_active
                                ? 'bg-surface-overlay hover:bg-yellow-800 border border-stroke-primary hover:border-yellow-700 text-text-secondary hover:text-yellow-300' :'bg-green-900/40 hover:bg-green-700 border border-green-800 hover:border-green-600 text-green-400 hover:text-white'
                            }`}
                            title={ad.is_active ? 'Pause' : 'Activate'}
                          >
                            {adToggleLoadingId === ad.id ? (
                              <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                            ) : ad.is_active ? (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Pause
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.069A1 1 0 0010 9.87v4.263a1 1 0 01-1.555.832l3.197-2.132a1 1 0 000-1.664z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Activate
                              </>
                            )}
                          </button>

                          {/* Delete */}
                          {adDeleteConfirmId === ad.id ? (
                            <div className="flex gap-1">
                              <button
                                onClick={() => handleDeleteAd(ad.id)}
                                disabled={adDeleteLoading}
                                className="text-xs font-bold px-2 py-1.5 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
                              >
                                {adDeleteLoading ? '…' : 'Yes'}
                              </button>
                              <button
                                onClick={() => setAdDeleteConfirmId(null)}
                                className="text-xs border border-stroke-primary text-text-secondary hover:text-text-primary px-2 py-1 rounded-[4px] transition-colors"
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={() => setAdDeleteConfirmId(ad.id)}
                              className="flex items-center gap-1 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-2.5 py-1.5 rounded-[4px] transition-colors"
                            >
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0016.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                              Delete
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── ADVERTISERS TAB ── */}
          {activeTab === 'advertisers' && (
            <div>
              <div className="flex items-center justify-between mb-5">
                <div>
                  <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">Advertiser Accounts</h3>
                  <p className="text-text-muted text-xs mt-0.5">Manage all advertiser accounts and their details</p>
                </div>
                <button onClick={() => openAdvertiserForm()} className="btn-primary text-sm">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Add Advertiser
                </button>
              </div>

              {advertisersError && <p className="text-brand-red text-sm mb-3">{advertisersError}</p>}

              {showAdvertiserForm && (
                <div className="mb-6 border border-stroke-primary bg-surface-secondary p-5 rounded-[4px]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-text-primary font-bold uppercase text-sm tracking-wide">{editingAdvertiser ? 'Edit Advertiser' : 'New Advertiser Account'}</h4>
                    <button onClick={() => { setShowAdvertiserForm(false); setEditingAdvertiser(null); }} className="text-text-secondary hover:text-text-primary">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Company Name *</label>
                      <input type="text" value={advertiserForm.company_name} onChange={e => setAdvertiserForm(p => ({ ...p, company_name: e.target.value }))} placeholder="Acme Corp" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Contact Name *</label>
                      <input type="text" value={advertiserForm.contact_name} onChange={e => setAdvertiserForm(p => ({ ...p, contact_name: e.target.value }))} placeholder="John Smith" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Email *</label>
                      <input type="email" value={advertiserForm.email} onChange={e => setAdvertiserForm(p => ({ ...p, email: e.target.value }))} placeholder="ads@company.com" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Phone</label>
                      <input type="text" value={advertiserForm.phone} onChange={e => setAdvertiserForm(p => ({ ...p, phone: e.target.value }))} placeholder="+1 555 000 0000" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Website</label>
                      <input type="text" value={advertiserForm.website} onChange={e => setAdvertiserForm(p => ({ ...p, website: e.target.value }))} placeholder="https://company.com" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Ad Budget ($)</label>
                      <input type="number" value={advertiserForm.ad_budget} onChange={e => setAdvertiserForm(p => ({ ...p, ad_budget: e.target.value }))} placeholder="0.00" className="input-field" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Notes</label>
                      <textarea value={advertiserForm.notes} onChange={e => setAdvertiserForm(p => ({ ...p, notes: e.target.value }))} placeholder="Internal notes..." rows={2} className="input-field resize-none" />
                    </div>
                  </div>
                  {advertiserFormError && <p className="text-brand-red text-sm mt-3">{advertiserFormError}</p>}
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSaveAdvertiser} disabled={advertiserFormLoading} className="btn-primary text-sm disabled:opacity-60">
                      {advertiserFormLoading ? 'Saving…' : editingAdvertiser ? 'Save Changes' : 'Create Account'}
                    </button>
                    <button onClick={() => { setShowAdvertiserForm(false); setEditingAdvertiser(null); }} className="text-text-secondary hover:text-text-primary text-sm border border-stroke-primary px-4 py-2 rounded-[4px] transition-colors">Cancel</button>
                  </div>
                </div>
              )}

              {advertisersLoading ? (
                <p className="text-text-muted text-sm">Loading advertisers…</p>
              ) : advertisers.length === 0 ? (
                <div className="text-center py-10 text-text-muted text-sm">No advertiser accounts yet. Add one above.</div>
              ) : (
                <div className="space-y-3">
                  {advertisers.map(adv => (
                    <div key={adv.id} className="border border-stroke-primary bg-surface-secondary rounded-[4px] p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-text-primary font-bold text-sm">{adv.company_name}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              adv.account_status === 'approved' ? 'bg-green-900 text-green-300' :
                              adv.account_status === 'rejected' ? 'bg-red-900 text-red-300' :
                              adv.account_status === 'suspended'? 'bg-yellow-900 text-yellow-300' : 'bg-surface-tertiary text-text-muted'
                            }`}>{adv.account_status.toUpperCase()}</span>
                          </div>
                          <p className="text-text-secondary text-xs">{adv.contact_name} · {adv.email}</p>
                          {adv.website && <p className="text-text-muted text-xs mt-0.5">{adv.website}</p>}
                          {adv.ad_budget ? <p className="text-text-muted text-xs mt-1">Budget: ${adv.ad_budget?.toLocaleString()}</p> : null}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                        <button onClick={() => openAdvertiserForm(adv)} className="text-text-muted hover:text-text-primary text-xs border border-stroke-primary px-2 py-1 rounded-[4px] transition-colors">Edit</button>
                        {adv.account_status !== 'suspended' && adv.account_status !== 'rejected' && (
                          <button onClick={() => handleSuspendAdvertiser(adv)} disabled={approvalActionLoading === adv.id} className="text-yellow-400 hover:text-yellow-300 text-xs border border-yellow-800 px-2 py-1 rounded-[4px] transition-colors disabled:opacity-50">
                            {approvalActionLoading === adv.id ? '…' : 'Suspend'}
                          </button>
                        )}
                        {adv.account_status === 'suspended' && (
                          <button onClick={() => handleSuspendAdvertiser(adv)} disabled={approvalActionLoading === adv.id} className="text-green-400 hover:text-green-300 text-xs border border-green-800 px-2 py-1 rounded-[4px] transition-colors disabled:opacity-50">
                            {approvalActionLoading === adv.id ? '…' : 'Reactivate'}
                          </button>
                        )}
                        {advertiserDeleteConfirmId === adv.id ? (
                          <div className="flex items-center gap-1">
                            <button onClick={() => handleDeleteAdvertiser(adv.id)} disabled={advertiserDeleteLoading} className="text-xs bg-brand-red text-white px-2 py-1 rounded-[4px] disabled:opacity-50">{advertiserDeleteLoading ? '…' : 'Confirm'}</button>
                            <button onClick={() => setAdvertiserDeleteConfirmId(null)} className="text-xs text-text-muted hover:text-text-primary px-2 py-1">Cancel</button>
                          </div>
                        ) : (
                          <button onClick={() => setAdvertiserDeleteConfirmId(adv.id)} className="text-text-muted hover:text-brand-red text-xs border border-stroke-primary px-2 py-1 rounded-[4px] transition-colors">Delete</button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── APPROVALS TAB ── */}
          {activeTab === 'approvals' && (
            <div>
              <div className="mb-5">
                <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">Ad Buyer Approvals</h3>
                <p className="text-text-muted text-xs mt-0.5">Review and approve or reject new advertiser applications</p>
              </div>

              {advertisersError && <p className="text-brand-red text-sm mb-3">{advertisersError}</p>}

              {advertisersLoading ? (
                <p className="text-text-muted text-sm">Loading…</p>
              ) : advertisers.filter(a => a.account_status === 'pending').length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-12 h-12 bg-surface-tertiary rounded-full flex items-center justify-center mx-auto mb-3">
                    <svg className="w-6 h-6 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <p className="text-text-muted text-sm">No pending applications. All caught up!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {advertisers.filter(a => a.account_status === 'pending').map(adv => (
                    <div key={adv.id} className="border border-yellow-800 bg-surface-secondary rounded-[4px] p-4">
                      <div className="flex items-start justify-between gap-3 mb-3">
                        <div>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-text-primary font-bold text-sm">{adv.company_name}</span>
                            <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-yellow-900 text-yellow-300">PENDING REVIEW</span>
                          </div>
                          <p className="text-text-secondary text-xs">{adv.contact_name} · {adv.email}</p>
                          {adv.phone && <p className="text-text-muted text-xs">{adv.phone}</p>}
                          {adv.website && <p className="text-text-muted text-xs">{adv.website}</p>}
                          {adv.ad_budget ? <p className="text-text-muted text-xs mt-1">Proposed Budget: <span className="text-text-secondary font-bold">${adv.ad_budget?.toLocaleString()}</span></p> : null}
                          {adv.notes && <p className="text-text-muted text-xs mt-1 italic">{adv.notes}</p>}
                          <p className="text-text-muted text-xs mt-1">Applied: {adv.created_at ? new Date(adv.created_at).toLocaleDateString() : 'N/A'}</p>
                        </div>
                      </div>

                      {rejectingId === adv.id ? (
                        <div className="mt-3 space-y-2">
                          <label className="text-text-muted text-xs uppercase tracking-wide block">Rejection Reason *</label>
                          <textarea
                            value={rejectionReason}
                            onChange={e => setRejectionReason(e.target.value)}
                            placeholder="Explain why this application is being rejected..."
                            rows={2}
                            className="input-field resize-none text-sm"
                          />
                          <div className="flex gap-2">
                            <button onClick={() => handleRejectAdvertiser(adv)} disabled={approvalActionLoading === adv.id} className="flex-1 flex items-center justify-center gap-1 bg-brand-red hover:bg-red-700 text-white text-xs font-bold px-3 py-2 rounded-[4px] transition-colors disabled:opacity-50">
                              {approvalActionLoading === adv.id ? 'Rejecting…' : 'Confirm Rejection'}
                            </button>
                            <button onClick={() => { setRejectingId(null); setRejectionReason(''); setAdvertisersError(''); }} className="text-text-muted hover:text-text-primary text-xs border border-stroke-primary px-3 py-2 rounded-[4px] transition-colors">Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => handleApproveAdvertiser(adv)} disabled={approvalActionLoading === adv.id} className="flex-1 flex items-center justify-center gap-1 bg-green-700 hover:bg-green-600 text-white text-xs font-bold px-3 py-2 rounded-[4px] transition-colors disabled:opacity-50">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                            {approvalActionLoading === adv.id ? 'Approving…' : 'Approve'}
                          </button>
                          <button onClick={() => setRejectingId(adv.id)} className="flex-1 flex items-center justify-center gap-1 bg-surface-overlay hover:bg-brand-red border border-stroke-primary hover:border-brand-red text-text-secondary hover:text-white text-xs font-bold px-3 py-2 rounded-[4px] transition-colors">
                            <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                            Reject
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Recently processed */}
              {advertisers.filter(a => a.account_status === 'approved' || a.account_status === 'rejected').length > 0 && (
                <div className="mt-8">
                  <h4 className="text-text-muted text-xs uppercase tracking-wide mb-3">Recently Processed</h4>
                  <div className="space-y-2">
                    {advertisers.filter(a => a.account_status === 'approved' || a.account_status === 'rejected').slice(0, 5).map(adv => (
                      <div key={adv.id} className="flex items-center justify-between border border-stroke-primary bg-surface-tertiary rounded-[4px] px-3 py-2">
                        <div>
                          <span className="text-text-primary text-xs font-bold">{adv.company_name}</span>
                          <span className="text-text-muted text-xs ml-2">{adv.email}</span>
                        </div>
                        <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${adv.account_status === 'approved' ? 'bg-green-900 text-green-300' : 'bg-red-900 text-red-300'}`}>
                          {adv.account_status.toUpperCase()}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── REVENUE TAB ── */}
          {activeTab === 'revenue' && (
            <div>
              {/* ── Stripe Ad Payments Section ── */}
              <div className="mb-8">
                <div className="flex items-center justify-between mb-4">
                  <div>
                    <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">Stripe Ad Payments</h3>
                    <p className="text-text-muted text-xs mt-0.5">All ad placements charged via Stripe — real-time revenue from advertisers</p>
                  </div>
                  <button
                    onClick={fetchAdPayments}
                    disabled={adPaymentsLoading}
                    className="flex items-center gap-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary text-xs font-bold uppercase px-3 py-2 rounded-[4px] transition-colors disabled:opacity-50"
                  >
                    <svg className={`w-3.5 h-3.5 ${adPaymentsLoading ? 'animate-spin' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </svg>
                    Refresh
                  </button>
                </div>

                {adPaymentsError && <p className="text-brand-red text-sm mb-3">{adPaymentsError}</p>}

                {/* Stripe revenue summary */}
                {adPayments.length > 0 && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
                    {[
                      {
                        label: 'Total Collected',
                        value: `$${(adPayments.filter(p => p.payment_status === 'succeeded').reduce((s, p) => s + p.amount, 0) / 100).toFixed(2)}`,
                        color: 'text-green-400',
                      },
                      {
                        label: 'Paid Campaigns',
                        value: adPayments.filter(p => p.payment_status === 'succeeded').length.toString(),
                        color: 'text-text-primary',
                      },
                      {
                        label: 'Pending',
                        value: adPayments.filter(p => p.payment_status === 'pending').length.toString(),
                        color: 'text-yellow-400',
                      },
                      {
                        label: 'Total Transactions',
                        value: adPayments.length.toString(),
                        color: 'text-text-primary',
                      },
                    ].map(stat => (
                      <div key={stat.label} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3 text-center">
                        <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                        <p className="text-text-muted text-xs uppercase tracking-wide mt-0.5">{stat.label}</p>
                      </div>
                    ))}
                  </div>
                )}

                {adPaymentsLoading ? (
                  <div className="space-y-2">
                    {[1, 2, 3].map(i => <div key={i} className="h-12 bg-surface-card border border-stroke-primary rounded-[4px] animate-pulse" />)}
                  </div>
                ) : adPayments.length === 0 ? (
                  <div className="text-center py-8 border border-stroke-primary rounded-[4px] bg-surface-card">
                    <p className="text-text-muted text-sm">No Stripe ad payments yet. Payments will appear here once advertisers purchase placements.</p>
                  </div>
                ) : (
                  <div className="border border-stroke-primary rounded-[4px] overflow-hidden">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-surface-secondary border-b border-stroke-primary">
                          <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Advertiser</th>
                          <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3 hidden sm:table-cell">Plan</th>
                          <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Amount</th>
                          <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3">Status</th>
                          <th className="text-left text-text-muted text-xs uppercase tracking-widest px-4 py-3 hidden md:table-cell">Date</th>
                        </tr>
                      </thead>
                      <tbody>
                        {adPayments.map((p, i) => (
                          <tr key={p.id} className={`border-b border-stroke-primary last:border-0 ${i % 2 === 0 ? 'bg-surface-card' : 'bg-surface-secondary'}`}>
                            <td className="px-4 py-3">
                              <p className="text-text-primary font-medium text-sm">{p.billing_name}</p>
                              <p className="text-text-muted text-xs">{p.billing_email}</p>
                              {p.company_name && <p className="text-text-muted text-xs italic">{p.company_name}</p>}
                            </td>
                            <td className="px-4 py-3 hidden sm:table-cell">
                              <p className="text-text-secondary text-xs capitalize">{p.plan?.replace(/-/g, ' ')}</p>
                              <p className="text-text-muted text-xs capitalize">{p.ad_type}</p>
                            </td>
                            <td className="px-4 py-3">
                              <span className="text-brand-red font-bold">${(p.amount / 100).toFixed(2)}</span>
                            </td>
                            <td className="px-4 py-3">
                              <span className={`text-xs font-bold uppercase px-2 py-0.5 rounded-[4px] border ${
                                p.payment_status === 'succeeded' ?'text-green-400 bg-green-400/10 border-green-400/30'
                                  : p.payment_status === 'pending' ?'text-yellow-400 bg-yellow-400/10 border-yellow-400/30' :'text-text-muted bg-surface-tertiary border-stroke-primary'
                              }`}>
                                {p.payment_status}
                              </span>
                            </td>
                            <td className="px-4 py-3 hidden md:table-cell text-text-muted text-xs">
                              {new Date(p.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div className="border-t border-stroke-primary pt-8 mt-2">
                <div className="flex items-center justify-between mb-5">
                  <div>
                    <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">Revenue Settlement Reports</h3>
                    <p className="text-text-muted text-xs mt-0.5">Track ad revenue, invoices, and payment status per advertiser</p>
                  </div>
                  <button onClick={() => setShowSettlementForm(p => !p)} className="btn-primary text-sm">
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                    New Report
                  </button>
                </div>

              {settlementsError && <p className="text-brand-red text-sm mb-3">{settlementsError}</p>}

              {/* Summary stats */}
              {settlements.length > 0 && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  {[
                    { label: 'Total Due', value: `$${settlements.reduce((s, r) => s + (r.amount_due || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: 'text-text-primary' },
                    { label: 'Total Paid', value: `$${settlements.reduce((s, r) => s + (r.amount_paid || 0), 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: 'text-green-400' },
                    { label: 'Pending', value: settlements.filter(r => r.payment_status === 'pending').length.toString(), color: 'text-yellow-400' },
                    { label: 'Overdue', value: settlements.filter(r => r.payment_status === 'overdue').length.toString(), color: 'text-brand-red' },
                  ].map(stat => (
                    <div key={stat.label} className="bg-surface-tertiary border border-stroke-primary rounded-[4px] p-3 text-center">
                      <p className={`text-lg font-bold ${stat.color}`}>{stat.value}</p>
                      <p className="text-text-muted text-xs uppercase tracking-wide mt-0.5">{stat.label}</p>
                    </div>
                  ))}
                </div>
              )}

              {showSettlementForm && (
                <div className="mb-6 border border-stroke-primary bg-surface-secondary p-5 rounded-[4px]">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-text-primary font-bold uppercase text-sm tracking-wide">Create Settlement Report</h4>
                    <button onClick={() => setShowSettlementForm(false)} className="text-text-secondary hover:text-text-primary">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
                    </button>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Advertiser *</label>
                      <select value={settlementForm.advertiser_id} onChange={e => setSettlementForm(p => ({ ...p, advertiser_id: e.target.value }))} className="input-field">
                        <option value="">Select advertiser…</option>
                        {advertisers.filter(a => a.account_status === 'approved').map(a => (
                          <option key={a.id} value={a.id}>{a.company_name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Period Start *</label>
                      <input type="date" value={settlementForm.period_start} onChange={e => setSettlementForm(p => ({ ...p, period_start: e.target.value }))} className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Period End *</label>
                      <input type="date" value={settlementForm.period_end} onChange={e => setSettlementForm(p => ({ ...p, period_end: e.target.value }))} className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Total Impressions</label>
                      <input type="number" value={settlementForm.total_impressions} onChange={e => setSettlementForm(p => ({ ...p, total_impressions: e.target.value }))} placeholder="0" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Total Clicks</label>
                      <input type="number" value={settlementForm.total_clicks} onChange={e => setSettlementForm(p => ({ ...p, total_clicks: e.target.value }))} placeholder="0" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Amount Due ($) *</label>
                      <input type="number" value={settlementForm.amount_due} onChange={e => setSettlementForm(p => ({ ...p, amount_due: e.target.value }))} placeholder="0.00" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Amount Paid ($)</label>
                      <input type="number" value={settlementForm.amount_paid} onChange={e => setSettlementForm(p => ({ ...p, amount_paid: e.target.value }))} placeholder="0.00" className="input-field" />
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Payment Status</label>
                      <select value={settlementForm.payment_status} onChange={e => setSettlementForm(p => ({ ...p, payment_status: e.target.value }))} className="input-field">
                        <option value="pending">Pending</option>
                        <option value="paid">Paid</option>
                        <option value="overdue">Overdue</option>
                        <option value="cancelled">Cancelled</option>
                      </select>
                    </div>
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Invoice Number</label>
                      <input type="text" value={settlementForm.invoice_number} onChange={e => setSettlementForm(p => ({ ...p, invoice_number: e.target.value }))} placeholder="INV-2026-001" className="input-field" />
                    </div>
                    <div className="md:col-span-2">
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Notes</label>
                      <textarea value={settlementForm.notes} onChange={e => setSettlementForm(p => ({ ...p, notes: e.target.value }))} placeholder="Additional notes..." rows={2} className="input-field resize-none" />
                    </div>
                  </div>
                  {settlementFormError && <p className="text-brand-red text-sm mt-3">{settlementFormError}</p>}
                  <div className="flex gap-3 mt-4">
                    <button onClick={handleSaveSettlement} disabled={settlementFormLoading} className="btn-primary text-sm disabled:opacity-60">
                      {settlementFormLoading ? 'Saving…' : 'Create Report'}
                    </button>
                    <button onClick={() => setShowSettlementForm(false)} className="text-text-secondary hover:text-text-primary text-sm border border-stroke-primary px-4 py-2 rounded-[4px] transition-colors">Cancel</button>
                  </div>
                </div>
              )}

              {settlementsLoading ? (
                <p className="text-text-muted text-sm">Loading settlements…</p>
              ) : settlements.length === 0 ? (
                <div className="text-center py-10 text-text-muted text-sm">
                  <p className="text-text-muted text-sm">Click "New Report" to create your first settlement</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {settlements.map(s => (
                    <div key={s.id} className="border border-stroke-primary bg-surface-secondary rounded-[4px] p-4">
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap mb-1">
                            <span className="text-text-primary font-bold text-sm">{s.advertisers?.company_name || 'Unknown'}</span>
                            <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                              s.payment_status === 'paid' ? 'bg-green-900 text-green-300' :
                              s.payment_status === 'overdue' ? 'bg-red-900 text-red-300' :
                              s.payment_status === 'cancelled'? 'bg-surface-tertiary text-text-muted' : 'bg-yellow-900 text-yellow-300'
                            }`}>{s.payment_status.toUpperCase()}</span>
                            {s.invoice_number && <span className="text-text-muted text-xs">{s.invoice_number}</span>}
                          </div>
                          <p className="text-text-secondary text-xs">{s.period_start} → {s.period_end}</p>
                          <div className="flex items-center gap-4 mt-1 flex-wrap">
                            <span className="text-text-muted text-xs">Due: <span className="text-text-primary font-bold">${(s.amount_due || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                            <span className="text-text-muted text-xs">Paid: <span className="text-green-400 font-bold">${(s.amount_paid || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></span>
                            {s.total_impressions > 0 && <span className="text-text-muted text-xs">{s.total_impressions.toLocaleString()} impressions</span>}
                            {s.total_clicks > 0 && <span className="text-text-muted text-xs">{s.total_clicks.toLocaleString()} clicks</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                          {s.payment_status !== 'paid' && s.payment_status !== 'cancelled' && (
                            <button onClick={() => handleMarkSettlementPaid(s)} disabled={settlementMarkPaidId === s.id} className="text-green-400 hover:text-green-300 text-xs border border-green-800 px-2 py-1 rounded-[4px] transition-colors disabled:opacity-50">
                              {settlementMarkPaidId === s.id ? '…' : 'Mark Paid'}
                            </button>
                          )}
                          {settlementDeleteConfirmId === s.id ? (
                            <div className="flex items-center gap-1">
                              <button onClick={() => handleDeleteSettlement(s.id)} disabled={settlementDeleteLoading} className="text-xs bg-brand-red text-white px-2 py-1 rounded-[4px] disabled:opacity-50">{settlementDeleteLoading ? '…' : 'Confirm'}</button>
                              <button onClick={() => setSettlementDeleteConfirmId(null)} className="text-xs text-text-muted hover:text-text-primary px-2 py-1">Cancel</button>
                            </div>
                          ) : (
                            <button onClick={() => setSettlementDeleteConfirmId(s.id)} className="text-text-muted hover:text-brand-red text-xs border border-stroke-primary px-2 py-1 rounded-[4px] transition-colors">Delete</button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
              </div>
            </div>
          )}

          {/* ── COMMENT MODERATION TAB ── */}
          {activeTab === 'comments' && (
            <div className="space-y-0">
              {/* Admin Add Comment Section */}
              <div className="p-6 border-b border-stroke-primary">
                <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide mb-1">✍️ Add Admin Comment</h3>
                <p className="text-text-muted text-sm mb-5">Post a comment as admin on a video or blog post. It will be auto-approved.</p>

                {/* Target type toggle */}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => { setAdminCommentTarget('video'); setAdminCommentError(''); }}
                    className={`text-xs font-bold px-4 py-2 rounded-[4px] border transition-colors ${adminCommentTarget === 'video' ? 'bg-brand-red text-white border-brand-red' : 'border-stroke-primary text-text-secondary hover:text-text-primary'}`}
                  >
                    🎬 Video
                  </button>
                  <button
                    onClick={() => { setAdminCommentTarget('blog'); setAdminCommentError(''); }}
                    className={`text-xs font-bold px-4 py-2 rounded-[4px] border transition-colors ${adminCommentTarget === 'blog' ? 'bg-brand-red text-white border-brand-red' : 'border-stroke-primary text-text-secondary hover:text-text-primary'}`}
                  >
                    📰 Blog Post
                  </button>
                </div>

                <div className="space-y-3">
                  {/* Target selector */}
                  {adminCommentTarget === 'video' ? (
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Video ID</label>
                      <select
                        value={adminCommentVideoId}
                        onChange={(e) => setAdminCommentVideoId(e.target.value)}
                        className="input-field text-sm"
                      >
                        <option value="">— Select a video —</option>
                        {allVideos.map((v) => (
                          <option key={v.id} value={v.id}>{v.title} ({v.id})</option>
                        ))}
                      </select>
                    </div>
                  ) : (
                    <div>
                      <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Blog Post</label>
                      <select
                        value={adminCommentPostId}
                        onChange={(e) => setAdminCommentPostId(e.target.value)}
                        className="input-field text-sm"
                      >
                        <option value="">— Select a blog post —</option>
                        {newsPosts.map((p) => (
                          <option key={p.id} value={p.id}>{p.title}</option>
                        ))}
                      </select>
                    </div>
                  )}

                  {/* Author name */}
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Author Name</label>
                    <input
                      type="text"
                      value={adminCommentAuthor}
                      onChange={(e) => setAdminCommentAuthor(e.target.value)}
                      placeholder="ViddStar Admin"
                      className="input-field text-sm"
                    />
                  </div>

                  {/* Comment body */}
                  <div>
                    <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Comment</label>
                    <textarea
                      value={adminCommentBody}
                      onChange={(e) => setAdminCommentBody(e.target.value)}
                      placeholder="Write your comment here..."
                      rows={4}
                      maxLength={1000}
                      className="input-field text-sm resize-none"
                    />
                    <p className="text-text-muted text-xs mt-1 text-right">{adminCommentBody.length}/1000</p>
                  </div>

                  {adminCommentError && (
                    <p className="text-brand-red text-sm">{adminCommentError}</p>
                  )}
                  {adminCommentSuccess && (
                    <p className="text-green-400 text-sm">✓ {adminCommentSuccess}</p>
                  )}

                  <button
                    onClick={handleAdminPostComment}
                    disabled={adminCommentLoading}
                    className="btn-primary text-sm px-5 py-2 disabled:opacity-50"
                  >
                    {adminCommentLoading ? (
                      <span className="flex items-center gap-2">
                        <span className="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
                        Posting...
                      </span>
                    ) : 'Post Comment'}
                  </button>
                </div>
              </div>

              {/* Existing moderation section */}
              <AdminCommentModeration />
            </div>
          )}

          {/* ── FLAGGED VIDEOS / REPORTS TAB ── */}
          {activeTab === 'reports' && (
            <div>
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
                <div>
                  <h3 className="text-text-primary font-bold text-lg uppercase tracking-wide">🚩 Flagged Videos</h3>
                  <p className="text-text-muted text-xs mt-0.5">Review reported content and take moderation actions</p>
                </div>
                <button
                  onClick={fetchReports}
                  disabled={reportsLoading}
                  className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors disabled:opacity-50"
                >
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Refresh
                </button>
              </div>

              {reportsError && (
                <div className="mb-4 px-4 py-2 bg-red-900/40 border border-red-700 text-red-300 text-sm rounded-[4px]">{reportsError}</div>
              )}

              {/* Filter tabs */}
              <div className="flex items-center gap-1 mb-4 border-b border-stroke-primary overflow-x-auto">
                {(['all', 'pending', 'reviewed', 'dismissed', 'removed'] as const).map(f => {
                  const count = f === 'all' ? reports.length : reports.filter(r => r.status === f).length;
                  return (
                    <button
                      key={f}
                      onClick={() => { setReportsFilter(f); setSelectedReportIds(new Set()); }}
                      className={`px-3 py-1.5 text-xs font-bold uppercase tracking-wide border-b-2 transition-colors whitespace-nowrap ${
                        reportsFilter === f ? 'border-brand-red text-text-primary' : 'border-transparent text-text-secondary hover:text-text-primary'
                      }`}
                    >
                      {f} {count > 0 && <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs leading-none ${f === 'pending' ? 'bg-orange-600 text-white' : 'bg-surface-tertiary text-text-muted'}`}>{count}</span>}
                    </button>
                  );
                })}
              </div>

              {/* Bulk actions bar */}
              {selectedReportIds.size > 0 && (
                <div className="flex items-center gap-3 mb-4 px-4 py-2.5 bg-surface-tertiary border border-stroke-primary rounded-[4px]">
                  <span className="text-text-secondary text-xs font-bold">{selectedReportIds.size} selected</span>
                  <div className="flex items-center gap-2 ml-auto">
                    <button
                      onClick={() => handleBulkReportAction('dismiss')}
                      disabled={bulkActionLoading}
                      className="text-xs font-bold px-3 py-1.5 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors disabled:opacity-50"
                    >
                      {bulkActionLoading ? 'Processing…' : 'Dismiss All'}
                    </button>
                    <button
                      onClick={() => handleBulkReportAction('remove')}
                      disabled={bulkActionLoading}
                      className="text-xs font-bold px-3 py-1.5 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-50"
                    >
                      {bulkActionLoading ? 'Processing…' : 'Remove All Videos'}
                    </button>
                    <button
                      onClick={() => setSelectedReportIds(new Set())}
                      className="text-xs text-text-muted hover:text-text-secondary transition-colors"
                    >
                      Clear
                    </button>
                  </div>
                </div>
              )}

              {reportsLoading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="w-6 h-6 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (() => {
                const filtered = reportsFilter === 'all' ? reports : reports.filter(r => r.status === reportsFilter);
                if (filtered.length === 0) {
                  return (
                    <div className="text-center py-12">
                      <div className="text-4xl mb-3">🚩</div>
                      <p className="text-text-muted text-sm">No {reportsFilter === 'all' ? '' : reportsFilter} reports found.</p>
                    </div>
                  );
                }

                const allFilteredIds = filtered.map(r => r.id);
                const allSelected = allFilteredIds.length > 0 && allFilteredIds.every(id => selectedReportIds.has(id));

                return (
                  <div className="space-y-3">
                    {/* Select all */}
                    <div className="flex items-center gap-2 px-1">
                      <input
                        type="checkbox"
                        checked={allSelected}
                        onChange={() => {
                          if (allSelected) {
                            setSelectedReportIds(prev => {
                              const next = new Set(prev);
                              allFilteredIds.forEach(id => next.delete(id));
                              return next;
                            });
                          } else {
                            setSelectedReportIds(prev => new Set([...prev, ...allFilteredIds]));
                          }
                        }}
                        className="w-4 h-4 accent-brand-red cursor-pointer"
                      />
                      <span className="text-text-muted text-xs">Select all ({filtered.length})</span>
                    </div>

                    {filtered.map(report => {
                      const isExpanded = expandedReportId === report.id;
                      const isSelected = selectedReportIds.has(report.id);
                      const statusColors: Record<string, string> = {
                        pending: 'bg-orange-900/40 text-orange-300 border-orange-700',
                        reviewed: 'bg-green-900/40 text-green-300 border-green-700',
                        dismissed: 'bg-surface-tertiary text-text-muted border-stroke-primary',
                        removed: 'bg-red-900/40 text-red-300 border-red-700',
                      };
                      const actionLabels: Record<string, string> = {
                        approved: '✅ Approved to stay',
                        video_removed: '🗑️ Video removed',
                        dismissed: '— Dismissed',
                        creator_suspended: '🔒 Creator suspended',
                      };

                      return (
                        <div
                          key={report.id}
                          className={`border rounded-[4px] transition-colors ${isSelected ? 'border-brand-red bg-surface-secondary' : 'border-stroke-primary bg-surface-secondary'}`}
                        >
                          {/* Report row */}
                          <div className="flex items-start gap-3 p-4">
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => {
                                setSelectedReportIds(prev => {
                                  const next = new Set(prev);
                                  if (next.has(report.id)) next.delete(report.id);
                                  else next.add(report.id);
                                  return next;
                                });
                              }}
                              className="w-4 h-4 mt-0.5 accent-brand-red cursor-pointer flex-shrink-0"
                            />

                            {/* Thumbnail */}
                            {report.video_thumbnail ? (
                              <img
                                src={report.video_thumbnail}
                                alt={report.video_title || 'Video thumbnail'}
                                className="w-16 h-10 object-cover rounded-[2px] flex-shrink-0 bg-surface-tertiary"
                              />
                            ) : (
                              <div className="w-16 h-10 bg-surface-tertiary rounded-[2px] flex-shrink-0 flex items-center justify-center">
                                <svg className="w-5 h-5 text-text-muted" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10l4.553-2.069A1 1 0 0121 8.82v6.36a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                </svg>
                              </div>
                            )}

                            {/* Info */}
                            <div className="flex-1 min-w-0">
                              <div className="flex flex-wrap items-center gap-2 mb-1">
                                <span className="text-text-primary text-sm font-bold truncate max-w-[200px]">{report.video_title}</span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full border ${statusColors[report.status] || statusColors.pending}`}>
                                  {report.status}
                                </span>
                                {report.action_taken && (
                                  <span className="text-xs text-text-muted">{actionLabels[report.action_taken] || report.action_taken}</span>
                                )}
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-text-muted">
                                <span>Category: <span className="text-text-secondary font-medium capitalize">{report.category.replace(/_/g, ' ')}</span></span>
                                {report.video_submitted_by && <span>Creator: <span className="text-text-secondary">{report.video_submitted_by}</span></span>}
                                {report.reporter_email && <span>Reporter: <span className="text-text-secondary">{report.reporter_email}</span></span>}
                                <span>{new Date(report.created_at).toLocaleDateString()}</span>
                              </div>
                              {report.details && (
                                <p className="text-text-muted text-xs mt-1 italic line-clamp-1">"{report.details}"</p>
                              )}
                            </div>

                            {/* Expand toggle */}
                            <button
                              onClick={() => setExpandedReportId(isExpanded ? null : report.id)}
                              className="flex-shrink-0 text-text-muted hover:text-text-primary transition-colors p-1"
                              title={isExpanded ? 'Collapse' : 'Expand actions'}
                            >
                              <svg className={`w-4 h-4 transition-transform ${isExpanded ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                              </svg>
                            </button>
                          </div>

                          {/* Expanded actions */}
                          {isExpanded && (
                            <div className="border-t border-stroke-primary px-4 py-4 space-y-3">
                              {report.details && (
                                <div>
                                  <p className="text-text-muted text-xs uppercase tracking-wide mb-1">Full Report Details</p>
                                  <p className="text-text-secondary text-sm bg-surface-tertiary border border-stroke-primary rounded-[4px] px-3 py-2">{report.details}</p>
                                </div>
                              )}

                              <div>
                                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Admin Notes (optional)</label>
                                <textarea
                                  value={reportAdminNotes[report.id] || ''}
                                  onChange={(e) => setReportAdminNotes(prev => ({ ...prev, [report.id]: e.target.value }))}
                                  placeholder="Internal notes about this report…"
                                  rows={2}
                                  className="input-field text-sm resize-none"
                                />
                              </div>

                              {report.status === 'pending' ? (
                                <div className="flex flex-wrap gap-2">
                                  <button
                                    onClick={() => handleReportAction(report, 'approve')}
                                    disabled={reportActionLoading === report.id}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-green-700 hover:bg-green-600 text-white rounded-[4px] transition-colors disabled:opacity-50"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                    Approve Video
                                  </button>
                                  <button
                                    onClick={() => handleReportAction(report, 'remove')}
                                    disabled={reportActionLoading === report.id}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-50"
                                  >
                                    <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                    Remove Video
                                  </button>
                                  {report.video_submitted_by && (
                                    suspendConfirmId === report.id ? (
                                      <div className="flex items-center gap-2">
                                        <span className="text-xs text-orange-400">Suspend creator?</span>
                                        <button
                                          onClick={() => handleReportAction(report, 'suspend')}
                                          disabled={reportActionLoading === report.id}
                                          className="text-xs font-bold px-3 py-2 bg-orange-700 hover:bg-orange-600 text-white rounded-[4px] transition-colors disabled:opacity-50"
                                        >
                                          Confirm
                                        </button>
                                        <button
                                          onClick={() => setSuspendConfirmId(null)}
                                          className="text-xs font-bold px-3 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    ) : (
                                      <button
                                        onClick={() => setSuspendConfirmId(report.id)}
                                        className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 border border-orange-700 text-orange-400 hover:bg-orange-900/30 rounded-[4px] transition-colors"
                                      >
                                        <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                                        </svg>
                                        Suspend Creator
                                      </button>
                                    )
                                  )}
                                  <button
                                    onClick={() => handleReportAction(report, 'dismiss')}
                                    disabled={reportActionLoading === report.id}
                                    className="flex items-center gap-1.5 text-xs font-bold px-3 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors disabled:opacity-50"
                                  >
                                    Dismiss
                                  </button>
                                  {reportActionLoading === report.id && (
                                    <div className="flex items-center gap-1.5 text-text-muted text-xs">
                                      <div className="w-3.5 h-3.5 border-2 border-brand-red border-t-transparent rounded-full animate-spin" />
                                      Processing…
                                    </div>
                                  )}
                                </div>
                              ) : (
                                <div className="flex items-center gap-2">
                                  <span className="text-text-muted text-xs">
                                    Resolved {report.resolved_at ? `on ${new Date(report.resolved_at).toLocaleDateString()}` : ''} {report.resolved_by ? `by ${report.resolved_by}` : ''}
                                  </span>
                                  {report.admin_notes && (
                                    <span className="text-text-muted text-xs italic">· Notes: {report.admin_notes}</span>
                                  )}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>
          )}

{/* ── GUIDELINES TAB ── */}
          {activeTab === 'guide' && (
            <div className="p-6 space-y-4">
              <h3 className="text-text-primary font-bold uppercase tracking-wide">Community Guidelines</h3>
              {[
                { icon: '✅', title: 'Approved Content', items: ['Viral moments & funny clips', 'Sports highlights & street sports', 'Music performances & freestyles', 'Feel-good & inspiring moments', 'News commentary & reactions', 'Street culture & entertainment'] },
                { icon: '❌', title: 'Rejected Content', items: ['Graphic violence or gore', 'Sexual or explicit content', 'Hate speech or discrimination', 'Harassment or bullying', 'Spam or misleading content', 'Copyright violations'] },
              ].map((section) => (
                <div key={section.title} className="border border-stroke-primary rounded-[4px] p-4">
                  <h4 className="text-text-primary font-bold text-sm mb-2">{section.icon} {section.title}</h4>
                  <ul className="space-y-1">
                    {section.items.map((item) => (
                      <li key={item} className="text-text-secondary text-sm flex items-center gap-2">
                        <span className="w-1 h-1 rounded-full bg-text-muted flex-shrink-0" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* ── EDIT STATS MODAL ── */}
      {editStatsVideo && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditStatsVideo(null)} />
          <div className="relative bg-surface-secondary border border-stroke-primary rounded-[8px] w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <h3 className="text-text-primary font-bold uppercase text-sm tracking-wide">Edit Stats</h3>
              <button onClick={() => setEditStatsVideo(null)} className="text-text-secondary hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-text-muted text-xs line-clamp-1 italic">{editStatsVideo.title}</p>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Views</label>
                <input
                  type="number"
                  min={0}
                  value={editStatsViews}
                  onChange={(e) => setEditStatsViews(Math.max(0, parseInt(e.target.value) || 0))}
                  className="input-field"
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Likes</label>
                <input
                  type="number"
                  min={0}
                  value={editStatsLikes}
                  onChange={(e) => setEditStatsLikes(Math.max(0, parseInt(e.target.value) || 0))}
                  className="input-field"
                />
              </div>
              {editStatsError && <p className="text-brand-red text-xs">{editStatsError}</p>}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-stroke-primary">
              <button
                onClick={() => setEditStatsVideo(null)}
                className="text-xs font-bold px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveStats}
                disabled={editStatsLoading}
                className="text-xs font-bold px-4 py-2 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
              >
                {editStatsLoading ? 'Saving…' : 'Save Stats'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── EDIT NEWS POST VIEWS MODAL ── */}
      {editNewsViewsPost && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => setEditNewsViewsPost(null)} />
          <div className="relative bg-surface-secondary border border-stroke-primary rounded-[8px] w-full max-w-sm shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <h3 className="text-text-primary font-bold text-sm">Edit Blog Post Views</h3>
              <button onClick={() => setEditNewsViewsPost(null)} className="text-text-muted hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4">
              <p className="text-text-muted text-xs line-clamp-1 italic">{editNewsViewsPost.title}</p>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">View Count</label>
                <input
                  type="number"
                  min={0}
                  value={editNewsViewsCount}
                  onChange={(e) => setEditNewsViewsCount(Math.max(0, parseInt(e.target.value) || 0))}
                  className="input-field"
                />
              </div>
              {editNewsViewsError && <p className="text-brand-red text-xs">{editNewsViewsError}</p>}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-stroke-primary">
              <button
                onClick={() => setEditNewsViewsPost(null)}
                className="text-xs font-bold px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveNewsViews}
                disabled={editNewsViewsLoading}
                className="text-xs font-bold px-4 py-2 bg-brand-red hover:bg-red-700 text-white rounded-[4px] transition-colors disabled:opacity-60"
              >
                {editNewsViewsLoading ? 'Saving…' : 'Save Views'}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* ── EDIT VIDEO MODAL ── */}
      {editVideoTarget && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/70" onClick={() => { setEditVideoTarget(null); setEditThumbnailFile(null); setEditThumbnailPreview(null); }} />
          <div className="relative bg-surface-secondary border border-stroke-primary rounded-[8px] w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between px-5 py-4 border-b border-stroke-primary">
              <h3 className="text-text-primary font-bold uppercase text-sm tracking-wide">Edit Video</h3>
              <button onClick={() => { setEditVideoTarget(null); setEditThumbnailFile(null); setEditThumbnailPreview(null); }} className="text-text-secondary hover:text-text-primary transition-colors">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="p-5 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Title</label>
                <input
                  type="text"
                  value={editVideoForm.title}
                  onChange={(e) => setEditVideoForm(f => ({ ...f, title: e.target.value }))}
                  className="input-field"
                  placeholder="Video title"
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Description</label>
                <textarea
                  value={editVideoForm.description}
                  onChange={(e) => setEditVideoForm(f => ({ ...f, description: e.target.value }))}
                  className="input-field min-h-[80px] resize-y"
                  placeholder="Video description"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Tag</label>
                  <input
                    type="text"
                    value={editVideoForm.tag}
                    onChange={(e) => setEditVideoForm(f => ({ ...f, tag: e.target.value }))}
                    className="input-field"
                    placeholder="e.g. Trending"
                  />
                </div>
                <div>
                  <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Category</label>
                  <select
                    value={editVideoForm.category}
                    onChange={(e) => setEditVideoForm(f => ({ ...f, category: e.target.value }))}
                    className="input-field"
                  >
                    <option value="">— Select —</option>
                    {VIDEO_CATEGORIES.map(cat => (
                      <option key={cat} value={cat}>{cat}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Status</label>
                <select
                  value={editVideoForm.video_status}
                  onChange={(e) => setEditVideoForm(f => ({ ...f, video_status: e.target.value }))}
                  className="input-field"
                >
                  <option value="pending">Pending</option>
                  <option value="approved">Approved</option>
                  <option value="rejected">Rejected</option>
                </select>
              </div>
              {/* Thumbnail section */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Thumbnail</label>
                {/* Current / preview */}
                {(editThumbnailPreview || editVideoForm.thumbnail) && (
                  <div className="mb-2 relative w-full aspect-video rounded-[4px] overflow-hidden border border-stroke-primary bg-black">
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={editThumbnailPreview || editVideoForm.thumbnail}
                      alt="Thumbnail preview"
                      className="w-full h-full object-cover"
                    />
                    {editThumbnailPreview && (
                      <span className="absolute top-1.5 left-1.5 bg-green-700 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">New</span>
                    )}
                  </div>
                )}
                {/* Upload button */}
                <input
                  ref={editThumbnailInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    setEditThumbnailFile(file);
                    const reader = new FileReader();
                    reader.onload = (ev) => setEditThumbnailPreview(ev.target?.result as string);
                    reader.readAsDataURL(file);
                  }}
                />
                <button
                  type="button"
                  onClick={() => editThumbnailInputRef.current?.click()}
                  className="w-full flex items-center justify-center gap-2 border border-dashed border-stroke-primary hover:border-green-600 text-text-secondary hover:text-green-400 text-xs font-bold py-2.5 rounded-[4px] transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  {editThumbnailFile ? 'Change Image' : 'Upload New Thumbnail'}
                </button>
                {editThumbnailFile && (
                  <button
                    type="button"
                    onClick={() => { setEditThumbnailFile(null); setEditThumbnailPreview(null); }}
                    className="mt-1.5 text-[11px] text-text-muted hover:text-brand-red transition-colors"
                  >
                    ✕ Remove new thumbnail
                  </button>
                )}
              </div>
              {/* Thumbnail URL (manual override) */}
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Thumbnail URL <span className="normal-case font-normal">(or paste a URL)</span></label>
                <input
                  type="text"
                  value={editVideoForm.thumbnail}
                  onChange={(e) => { setEditVideoForm(f => ({ ...f, thumbnail: e.target.value })); setEditThumbnailFile(null); setEditThumbnailPreview(null); }}
                  className="input-field"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-text-muted text-xs uppercase tracking-wide block mb-1.5">Thumbnail Alt Text</label>
                <input
                  type="text"
                  value={editVideoForm.thumbnail_alt}
                  onChange={(e) => setEditVideoForm(f => ({ ...f, thumbnail_alt: e.target.value }))}
                  className="input-field"
                  placeholder="Describe the thumbnail image"
                />
              </div>
              {editVideoError && <p className="text-brand-red text-xs">{editVideoError}</p>}
            </div>
            <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-stroke-primary">
              <button
                onClick={() => { setEditVideoTarget(null); setEditThumbnailFile(null); setEditThumbnailPreview(null); }}
                className="text-xs font-bold px-4 py-2 border border-stroke-primary text-text-secondary hover:text-text-primary rounded-[4px] transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEditVideo}
                disabled={editVideoLoading}
                className="text-xs font-bold px-4 py-2 bg-green-700 hover:bg-green-600 text-white rounded-[4px] transition-colors disabled:opacity-60"
              >
                {editVideoLoading ? 'Saving…' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}